import { bindClickWheelZoom } from "../utils/clickWheelZoom";
import StringShadingSummary from "./StringShadingSummary";
import { topologyErrors } from "../utils/detailedShading";
import { scenePreparationMessage } from "../utils/scenePreparationIssue";
import { resolveSceneModel } from "../utils/sceneModel";
import { parcelFrame, buildRows } from "../utils/sceneRows";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { sceneCalculationState, subscribeSceneCalculation } from "../services/sceneCalculation";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import MaybeTooltip from "./MaybeTooltip";
import {
  sub, mul, dot, cross, norm, clamp, sunVector, sunAboveGround,
} from "../utils/vec3";
import { drawShadingScene } from "../utils/sceneDraw";
import { pickSceneObject, sceneDragDelta } from "../utils/sceneInteraction";
import { layoutGeometry, moduleQuads, snapModuleDelta, overlapCount } from "../utils/moduleLayout";
import { parseSTL, placeMesh, boundsOf, describeMesh, UNIT_SCALES } from "../utils/stlLoader";
import { loadMesh, saveMesh, deleteMesh } from "../utils/meshCache";
import { prepareWholePlant, describePlant, measureLayout } from "../utils/meshArray";
import { describeRows } from "../utils/fieldGeometry";
import { buildSiteGeometry, checkRoofRows, combineTriangles, obstacleFootprint, siteGeometryErrors } from "../utils/siteGeometry";
import { useIsGreek } from "../context/LanguageContext";
import { moduleSamples, groupModuleShading } from "../utils/moduleShading";
import { withModuleValues } from "../utils/moduleShadingCodec";
import { buildProjectNeighbours, projectRoofFaces, rowsMesh, sceneOffset } from "../utils/projectScene";
import { moduleClearanceGuides } from "../utils/moduleClearances";
import {
  blockedFraction,
  buildShadingTableAsync,
  lookupShadingTable,
  sunDirection,
} from "../utils/shadingTable";
import {
  solarPosition, daylightWindow, instantFromLocal, zoneOffsetHours,
} from "../utils/solarPosition";
import {
  normalizeHorizon, beamBlockedByHorizon, describeHorizon,
} from "../utils/horizonProfile";
import {
  shadedFraction1d,
  bypassedFraction,
  incrementalElectricalLoss,
  beamLossFraction,
  obstacleBeamLoss,
  trackerRotation,
} from "../utils/rowShading";

/**
 * Row-to-row shading, drawn.
 *
 * The layout page could always tell you the pitch and the GCR. It could not
 * tell you what they do at 09:00 on 21 December, which is the question the
 * numbers exist to answer and the one a spacing decision actually turns on.
 *
 * Two things are on screen and they are not the same thing:
 *
 *   the SHADOWS on the ground are true 3D projections of this row geometry
 *   along the sun vector - real perspective, real terrain slope;
 *
 *   the BAND across the modules and every number in the readout come from the
 *   engine's own model (pvlib's shaded_fraction1d and pv_core/row_shading.py,
 *   ported in ../utils/rowShading.js and held to pvlib's values by test). That
 *   model treats the rows as infinitely long, so it has no end-of-row effects.
 *
 * Drawing a prettier, more detailed shadow on the modules than the model
 * computes would look better and mislead, so the band is deliberately the
 * model's flat fraction. The end rows of the drawn slice would in reality see
 * less shade than the middle ones; the model says otherwise and the model is
 * what produces the yield.
 */

// How many rows the slice shows. Five is enough to see a shadow reach the row
// behind and still leave the geometry legible; the number is editable because
// "is that all of them?" is the first thing anyone asks. Two is the fewest that
// can show a shadow reaching the next row; the upper bound is where the rows
// become hairlines and the drawing stops being worth doing.
const MIN_DRAWN_ROWS = 2;
const MAX_DRAWN_ROWS = 40;
const DEFAULT_ROW_COUNT = 5;
// The scene's height follows the window rather than a fixed 560px: at full
// screen a fixed canvas left a third of the height empty, and the whole point
// of the view is to see row shadows at a legible size. Bounded so a short
// laptop screen still shows the controls beneath it.
const MIN_CANVAS_HEIGHT = 460;
const MAX_CANVAS_HEIGHT = 900;
const canvasHeight = () => {
  const vh = (typeof window !== "undefined" && window.innerHeight) || 900;
  return Math.round(Math.min(MAX_CANVAS_HEIGHT, Math.max(MIN_CANVAS_HEIGHT, vh * 0.62)));
};


/**
 * A real number, or null.
 *
 * Number(null) is 0 and Number("") is 0, both perfectly finite, so a bare
 * Number.isFinite check treats "no value" as midnight UTC - which is how a
 * missing timezone becomes a clock that is silently two hours wrong and says
 * nothing about it.
 */
const finiteOrNull = (v) =>
  v === null || v === undefined || v === "" || !Number.isFinite(Number(v))
    ? null
    : Number(v);
const fmtDeg = (v) => `${v >= 0 ? "" : "-"}${Math.abs(v).toFixed(1)}°`;
const pct = (v) => `${(100 * v).toFixed(v >= 0.1 ? 0 : 1)}%`;

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Day of year (1-365, no leap day) to {month, day}. */
function fromDayOfYear(doy) {
  let d = clamp(Math.round(doy), 1, 365);
  for (let m = 0; m < 12; m += 1) {
    if (d <= MONTH_DAYS[m]) return { month: m + 1, day: d };
    d -= MONTH_DAYS[m];
  }
  return { month: 12, day: 31 };
}

function toDayOfYear(month, day) {
  let doy = day;
  for (let m = 0; m < month - 1; m += 1) doy += MONTH_DAYS[m];
  return doy;
}

const clockLabel = (minutes) => {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

// Dates a shading study is actually argued over. The winter solstice is the
// worst case in the northern hemisphere and the mildest in the southern, which
// is why the label follows the hemisphere rather than asserting "worst case".
const DATE_PRESETS = [
  { key: "dec", label: "21 Dec", month: 12, day: 21 },
  { key: "mar", label: "20 Mar", month: 3, day: 20 },
  { key: "jun", label: "21 Jun", month: 6, day: 21 },
];

/**
 * Everything the scene needs, resolved from the saved layout.
 *
 * Returns null when the layout is not complete enough to draw: a viewer that
 * invents a pitch would be worse than one that says it cannot draw yet.
 */
function useSceneModel({
  latitude,
  totalModules,
  longitude,
  surfaceTilt,
  surfaceAzimuth,
  pitch,
  rowVerticalModules,
  moduleLength,
  moduleWidth,
  groundClearance,
  fieldRows,
  rowModules,
  parcelTables,
  parcelOutline,
  arrayOffsetEast = 0,
  arrayOffsetNorth = 0,
  slopeTilt,
  slopeAzimuth,
  portrait,
  halfCut,
  bypassDiodes,
  nearShadingModel,
  electricalEffect,
  mountType,
  axisOrientation,
  maxAngle,
  backTrack,
  installationType,
  siteGeometry,
  roofPitch = 0,
  roofAzimuth = 180,
}) {
  return useMemo(() => resolveSceneModel({ fieldRows, rowModules, parcelTables, parcelOutline, arrayOffsetEast, arrayOffsetNorth,
    latitude, longitude, surfaceTilt, surfaceAzimuth, pitch, rowVerticalModules,
    moduleLength, moduleWidth, groundClearance, slopeTilt, slopeAzimuth,
    portrait, halfCut, bypassDiodes, nearShadingModel, electricalEffect,
    mountType, axisOrientation, maxAngle, backTrack, totalModules,
    installationType, siteGeometry, roofPitch, roofAzimuth, }), [
    fieldRows, rowModules, parcelTables, parcelOutline, arrayOffsetEast, arrayOffsetNorth,
    latitude, longitude, surfaceTilt, surfaceAzimuth, pitch, rowVerticalModules,
    moduleLength, moduleWidth, groundClearance, slopeTilt, slopeAzimuth,
    portrait, halfCut, bypassDiodes, nearShadingModel, electricalEffect,
    mountType, axisOrientation, maxAngle, backTrack, totalModules,
    installationType, siteGeometry, roofPitch, roofAzimuth,
  ]);
}


/**
 * The site's ground plane.
 *
 * `slopeAzimuth` is the direction the ground faces, i.e. downhill, so height
 * falls off along it. Flat or unknown terrain gives a plane at z = 0, which is
 * the previous behaviour exactly.
 */
/** Row rotation now: the tilt for a fixed plane, the tracked angle otherwise. */
function rowRotationAt(scene, sun) {
  if (!scene.tracker) return scene.surfaceTilt;
  const theta = trackerRotation({
    solarZenith: sun.zenith,
    solarAzimuth: sun.azimuth,
    axisTilt: scene.axisTilt,
    axisAzimuth: scene.axisAzimuth,
    maxAngle: scene.maxAngle,
    backtrack: scene.backtrack,
    gcr: scene.gcr,
    crossAxisTilt: scene.crossAxisSlope,
  });
  return theta == null ? 0 : theta;
}

/**
 * The same state, from a shaded fraction measured some other way.
 *
 * Whole-plant mode gets its fraction by casting rays at an imported model
 * instead of evaluating the row formula, but everything downstream of the
 * fraction - which substrings a bypass diode switches out, what that costs the
 * beam - is unchanged and must stay unchanged. Sharing this is what stops the
 * two modes quietly disagreeing about a diode.
 */
function stateFromFraction(scene, rotation, shaded, worstRow = null) {
  const opts = {
    verticalModules: scene.verticalModules,
    portrait: scene.module.portrait,
    halfCut: scene.module.halfCut,
    bypassDiodes: scene.module.bypassDiodes,
    model: scene.model,
    electricalEffect: scene.electricalEffect,
  };
  // `shaded` here is a share of the WHOLE field's module area, not of one row's
  // slant height, so it must not go through the quantiser directly: that turned
  // a tree over 2% of the field into half the array's beam. With the per-row
  // maximum the worst row is quantised and weighted by the share of the field
  // affected -- the same formula the engine applies to the stored table.
  const total =
    worstRow == null
      ? beamLossFraction(shaded, opts)
      : obstacleBeamLoss(shaded, worstRow, opts);
  return {
    rotation,
    shaded,
    bypassed: bypassedFraction(shaded, opts),
    // Row mode keeps its own definition of the increment; the obstacle path has
    // no linear part already removed upstream, so there the increment is simply
    // what the diodes add over the area actually blocked.
    extra:
      worstRow == null
        ? incrementalElectricalLoss(shaded, opts)
        : Math.max(0, total - shaded),
    total,
    night: false,
  };
}

/** Shading state for one instant: the engine's model, not a second opinion. */
function shadingAt(scene, sun) {
  const rotation = rowRotationAt(scene, sun);
  if (sun.zenith >= 90) {
    return { rotation, shaded: 0, bypassed: 0, extra: 0, total: 0, night: true };
  }
  const shaded = shadedFraction1d({
    solarZenith: sun.zenith,
    solarAzimuth: sun.azimuth,
    axisAzimuth: scene.axisAzimuth,
    shadedRowRotation: rotation,
    collectorWidth: scene.collectorWidth,
    pitch: scene.pitch,
    // Both terrain angles, or neither. The cross-axis slope moves the row in
    // front up or down; the axis tilt turns the plane the projected solar
    // zenith angle is measured in. Passing only the first described rows
    // stepping down a hillside on a level axis - a shape the ground cannot
    // take - and on the reviewed 4.3 deg site it reported 0.1% shading at
    // 16:00 on the solstice where the geometry gives 12.7%.
    axisTilt: scene.axisTilt,
    crossAxisSlope: scene.crossAxisSlope,
  });
  const opts = {
    verticalModules: scene.verticalModules,
    portrait: scene.module.portrait,
    halfCut: scene.module.halfCut,
    bypassDiodes: scene.module.bypassDiodes,
    model: scene.model,
    electricalEffect: scene.electricalEffect,
  };
  return {
    rotation,
    shaded,
    bypassed: bypassedFraction(shaded, opts),
    extra: incrementalElectricalLoss(shaded, opts),
    total: beamLossFraction(shaded, opts),
    night: false,
  };
}

export default function ShadingScene3D(props) {
  const theme = useTheme();
  const greek = useIsGreek();
  const tr = (el, en) => greek ? el : en;
  // A dual-axis tracker turns every module to the sun on its own; the layout's
  // shading describes fixed rows and single-axis trackers only, so it is not
  // offered (the engine would not apply it either).
  const dualAxis = String(props.mountType || "").toLowerCase() === "dual_axis";
  const horizonDescription = (points) => {
    if (!points) return tr("χωρίς προφίλ ορίζοντα", "no terrain profile");
    if (!greek) return describeHorizon(points);
    const max = points.reduce((highest, [, elevation]) => Math.max(highest, elevation), 0);
    const peak = points.find(([, elevation]) => elevation === max);
    return `${points.length} σημεία, μέγιστο ύψος ${max.toFixed(1)}° στο αζιμούθιο ${peak[0].toFixed(0)}°`;
  };
  const [localSampleResolution, setSampleResolution] = useState(() => props.savedTable?.table?.samplesPerModule === 64 ? 8 : 4);
  const sampleResolution = props.samplesPerModule != null ? (props.samplesPerModule === 64 ? 8 : 4) : localSampleResolution;
  const sceneWorkspace = useRef({});
  const isDark = theme.palette.mode === "dark";
  const scene = useSceneModel(props);
  const {
    utcOffsetHours = null,
    timeZoneLabel = null,
    // The table already stored with the project, if any, and the way to store a
    // new one. Without the callback the panel still measures and reports - it
    // just cannot hand the result to the engine.
    savedTable = null,
    // savedTable is a calculation the layout holds but has not saved yet: its
    // Save stores it. Said so, so "calculated" is not read as "stored".
    savedTableUnsaved = false,
    // Which project's cached model to look for. Absent (a preview, a story, a
    // test) simply means no cache: everything else still works.
    projectId = null,
    onObstacleTableChange = null,
    // Several faces: the button calculates every face at once through the
    // layout (onMeasureAll({ placement, onProgress, shouldCancel })), since
    // each one's shading depends on the others; measureAllCount of them.
    onMeasureAll = null,
    measureAllCount = 1,
    // Told where an imported model stands on this face (null: none), so the
    // layout knows what a calculation was made with.
    onPlacementChange = null,
  onLayoutFromModel = null,
    // The terrain profile the engine already applies. Drawing it is what stops
    // the picture being more optimistic than the model.
    horizonProfile = null,
    // fieldRows / rowModules / parcelTables / parcelOutline used to be declared
    // here and read nowhere. The scene takes its field from `scene.field` and
    // its parcel from `geo.boundary`, so the props were four defaults pretending
    // to be an interface. Removed rather than left to imply a contract.
  horizonEnabled = true,
  onHorizonEnabledChange = null,
  // A ref the layout tab fills with a function that draws the scene off-screen
  // at any date and time. The layout sheet uses it to show the array at the
  // hours the spacing was solved for, without the user having to scrub to them.
  snapshotRef = null,
  // Ground or roof. It decides what the model's lowest point means: on the
  // ground it is the ground, and the file is dropped onto it; on a roof the
  // file's own zero is the roof the array stands on, and nothing is dropped.
  installationType = "ground",
  siteGeometry = null,
  onRoofStatus = null,
  // The plant's OTHER orientations, drawn where their tables stand for
  // context (utils/contextPlanes). They cast no shadow here and take none.
  contextPlanes = null,
  } = props;
  const onRoof = String(installationType || "").startsWith("roof");

  // The profile as measured, whether or not it is being applied. Kept apart
  // from `horizon` so the switch can say "there is one, it is off" rather than
  // disappearing along with the thing it controls.
  const horizonAvailable = useMemo(
    () => normalizeHorizon(horizonProfile), [horizonProfile]
  );
  const horizonOn = horizonEnabled !== false;
  const horizon = horizonOn ? horizonAvailable : null;

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const projectionRef = useRef(null);
  const [editMode, setEditMode] = useState(props.workspaceViewRef?.current?.editMode || false);
  const [selectedObject, setSelectedObject] = useState("");
  const [lastEdit, setLastEdit] = useState(null);
  const [editingDrag, setEditingDrag] = useState(false);
  const [editHint, setEditHint] = useState("");
  const [individualEdit, setIndividualEdit] = useState(props.workspaceViewRef?.current?.individualEdit || false);
  const [selectedModules, setSelectedModules] = useState([]);
  const [snapEnabled, setSnapEnabled] = useState(true);
  // The generated rows sit edge to edge; a module snapped beside one sits the
  // same way unless a gap is asked for.
  const [snapGap, setSnapGap] = useState(0);
  // Where the last snap put the dragged modules: { slots, target, kind },
  // outlined in the scene so it is plain that, and against what, it snapped.
  const [snapInfo, setSnapInfo] = useState(null);
  // A dragged module moves HERE, in a preview, and reaches the layout once, on
  // release. Sent up on every pointer move, each step re-rendered the whole
  // layout dialog, re-packed its roof array and re-ran the roof checks - on a
  // 708-module roof that is well over a frame per move, and the panel trailed
  // the pointer. The picture reads the preview; validity, shading and the
  // saved layout read the committed offsets.
  const [dragOffsets, setDragOffsets] = useState(null);
  const moduleOffsets = dragOffsets ?? props.moduleOffsets;
  const commitOffsetsRef = useRef(null);
  commitOffsetsRef.current = props.onModuleOffsetsChange;
  const hasModuleMoves = Object.keys(props.moduleOffsets || {}).length > 0 || (Array.isArray(props.excludedSlots) && props.excludedSlots.length > 0);
  const [viewMode, setViewMode] = useState(props.workspaceViewRef?.current?.viewMode === "2d" ? "2d" : "3d");
  const last3dElevation = useRef(props.workspaceViewRef?.current?.last3dElevation ?? props.workspaceViewRef?.current?.camera?.el ?? 24);
  const [camera, setCamera] = useState(() => {
    const saved = props.workspaceViewRef?.current;
    return { az: 205, zoom: 1, panX: 0, panY: 0, ...saved?.camera, el: saved?.viewMode === "2d" ? 90 : (saved?.camera?.el ?? 24) };
  });
  useEffect(() => {
    if (props.workspaceViewRef) props.workspaceViewRef.current = { ...props.workspaceViewRef.current, camera, viewMode, last3dElevation: viewMode === "3d" ? camera.el : last3dElevation.current, editMode, individualEdit };
  }, [props.workspaceViewRef, camera, viewMode, editMode, individualEdit]);
  const [dateDoy, setDateDoy] = useState(() => toDayOfYear(12, 21));
  const [minutes, setMinutes] = useState(11 * 60);
  const [playing, setPlaying] = useState(false);
  const [showSegments, setShowSegments] = useState(false);
  const lineTopology = useMemo(() => props.moduleTopology && !topologyErrors(props.moduleTopology).length
    ? props.moduleTopology : null, [props.moduleTopology]);
  const [rowCount, setRowCountRaw] = useState(DEFAULT_ROW_COUNT);
  const [rowsTouched, setRowsTouched] = useState(false);
  const setRowCount = useCallback((n) => {
    setRowsTouched(true);
    setRowCountRaw(n);
  }, []);
  // The text as typed, kept apart from the committed number so that clearing
  // the box or passing through "1" on the way to "12" does not fight the user.
  const [rowsInput, setRowsInput] = useState(String(DEFAULT_ROW_COUNT));

  // How many rows there are to draw, and how many are worth drawing at once.
  const fieldRowCount = scene?.field?.rows || null;
  const minDrawnRows = fieldRowCount === 1 ? 1 : MIN_DRAWN_ROWS;
  const maxDrawnRows = Math.min(MAX_DRAWN_ROWS, fieldRowCount || MAX_DRAWN_ROWS);
  const suggestedRows = fieldRowCount
    ? clamp(Math.min(fieldRowCount, 8), minDrawnRows, maxDrawnRows)
    : DEFAULT_ROW_COUNT;

  // Follows the field until someone types a number of their own, and never
  // offers more rows than the plant has.
  useEffect(() => {
    if (rowsTouched) {
      const capped = clamp(rowCount, minDrawnRows, maxDrawnRows);
      if (capped !== rowCount) {
        setRowCountRaw(capped);
        setRowsInput(String(capped));
      }
      return;
    }
    if (suggestedRows !== rowCount) {
      setRowCountRaw(suggestedRows);
      setRowsInput(String(suggestedRows));
    }
  }, [suggestedRows, minDrawnRows, maxDrawnRows, rowCount, rowsTouched]);
  // The imported obstacle, if any: the raw triangles as parsed, plus how the
  // user has told us to interpret them. Kept separate so changing the units or
  // the rotation never re-reads the file.
  // Filling the window is the difference between reading this scene and
  // squinting at it: the whole point of the view is spatial, and a 230px canvas
  // in a form column cannot show a shadow crossing six rows. The site-model
  // importer is deliberately left behind -- it is a file picker and a
  // measurement run, not something anyone needs at full size, and carrying it
  // in would put an upload button over a picture someone is reading.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    // The page behind must not scroll under a fixed overlay.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [fullscreen]);

  const [meshFile, setMeshFile] = useState(null);
  const [placement, setPlacement] = useState({
    upAxis: "z",
    units: "m",
    northRotationDeg: 0,
    east: 0,
    // Default it south of the array, where an obstacle actually matters in the
    // northern hemisphere. It is the first thing anyone will move anyway.
    north: -25,
    baseHeight: 0,
    // Always true before, and with no control to change it. A rooftop model
    // drawn with its roof at zero and its walls going down was lifted until
    // its street level met the array -- which put the roof, and every parapet
    // and chimney on it, a whole building above the modules. It looked
    // plausible and was wrong by the height of the building.
    dropToGround: !onRoof,
  });
  // Follows a change of installation type made while the scene is open; a
  // user who has set it by hand keeps their setting until the type moves.
  useEffect(() => {
    setPlacement((p) => ({ ...p, dropToGround: !onRoof }));
  }, [onRoof]);
  /**
   * The stored measurement's placement, back in the fields.
   *
   * The placement travels with the table and nothing read it back, so after a
   * refresh the boxes reset to the defaults while the project went on applying
   * a measurement taken somewhere else entirely. Drop the same file in again
   * and the tree stood 15 m from where it was measured, with no sign that the
   * two disagreed -- the picture showing one site and the yield another.
   *
   * Keyed on the VALUE rather than the object: the parent rebuilds the config
   * on most renders, and an effect that depended on the object's identity would
   * overwrite the boxes under the user's hands as they typed.
   */
  const savedPlacementKey = savedTable?.placement
    ? JSON.stringify(savedTable.placement)
    : "";
  useEffect(() => {
    if (!savedPlacementKey) return;
    try {
      const stored = JSON.parse(savedPlacementKey);
      setPlacement((current) => ({ ...current, ...stored }));
    } catch {
      // A placement that will not parse is not worth taking the panel down for;
      // the defaults are a usable starting point and the fields are editable.
    }
  }, [savedPlacementKey]);

  const [meshError, setMeshError] = useState("");
  const [tableProgress, setTableProgress] = useState(null);
  // While the application-wide dialog shows the calculation, its progress and
  // its cancel button live there; the row below keeps only its own run.
  const overlayActive = useSyncExternalStore(subscribeSceneCalculation, sceneCalculationState, sceneCalculationState) != null;
  const [allError, setAllError] = useState("");
  const [table, setTable] = useState(null);
  const tableApplied = useMemo(() => Boolean(table && savedTable?.table &&
    JSON.stringify(table) === JSON.stringify(savedTable.table)), [table, savedTable]);
  const cancelRef = useRef(false);
  const storeOnMeasureRef = useRef(false);
  const fileInputRef = useRef(null);
  const [resizeTick, setResizeTick] = useState(0);

  // Longitude gives the zone meridian when the real offset is unknown. Better
  // than assuming UTC, and stated as approximate rather than passed off as the
  // project's clock.
  const knownOffset = finiteOrNull(utcOffsetHours);
  // The project's STANDARD offset - what /geo/utc-offset returns, and the base
  // the simulation's hourly series sits on.
  const standardOffsetHours = useMemo(() => {
    if (knownOffset != null) return knownOffset;
    if (scene && Number.isFinite(scene.lon)) return Math.round(scene.lon / 15);
    return 0;
  }, [knownOffset, scene]);

  const { month, day } = useMemo(() => fromDayOfYear(dateDoy), [dateDoy]);

  /**
   * The clock this panel runs on: the site's own, daylight saving included.
   *
   * It used to be the standard offset all year, to match the time base the
   * simulation's hourly series sits on. That is a defensible thing for the model
   * to do and a poor thing to put on screen: on 21 June it put sunset at 19:59
   * for a site whose sunset everybody knows as 20:59, and a reader has no way to
   * tell a one-hour convention from a one-hour bug. The offset is printed next
   * to every time instead, so the convention is visible rather than assumed -
   * and where the two differ the tooltip names the simulation's base as well.
   */
  const offsetForDate = useCallback((m, d) => {
    if (!timeZoneLabel) return standardOffsetHours;
    // Midday, so the seed offset being an hour out cannot land on the wrong
    // side of a transition - those happen in the small hours.
    const zone = zoneOffsetHours(
      timeZoneLabel,
      instantFromLocal(2026, m, d, 12 * 60, standardOffsetHours)
    );
    return zone == null ? standardOffsetHours : zone;
  }, [timeZoneLabel, standardOffsetHours]);
  const offsetHours = useMemo(
    () => offsetForDate(month, day), [offsetForDate, month, day]
  );

  /**
   * How far the site's wall clock is from the clock this panel uses, on this
   * date. Zero in winter and wherever daylight saving is not observed.
   *
   * The panel deliberately stays on standard time, because the simulation does.
   * Saying so in a tooltip was not enough: a summer sunset an hour before the
   * one everybody at the site knows reads as a bug, and a reader cannot check
   * the picture against reality without doing the arithmetic themselves.
   */
  /** The mesh in world coordinates, re-placed whenever a control moves. */
  const placed = useMemo(() => {
    if (!meshFile) return null;
    try {
      return placeMesh(meshFile.triangles, {
        ...placement,
        // Onto the terrain, not onto the horizontal datum: on a slope the two
        // are metres apart, and a shadow that starts in the wrong place is
        // worse than no shadow at all.
        groundZAt: scene?.terrain?.groundZ,
      });
    } catch {
      // A placement that cannot be computed (a degenerate mesh, say) simply
      // draws nothing rather than taking the panel down with it.
      return null;
    }
  }, [meshFile, placement, scene]);
  // The other planes' roof faces, so the walls where this face meets them
  // are left out of the picture (utils/siteGeometry).
  const siteMesh = useMemo(() => buildSiteGeometry(siteGeometry, scene?.terrain?.groundZ, {
    otherFaces: projectRoofFaces(contextPlanes, { siteGeometry }).filter((f) => f.owner != null),
  }), [siteGeometry, scene, contextPlanes]);
  const neighbours = useMemo(() => buildProjectNeighbours(contextPlanes, {
    siteGeometry, origin: scene?.parcel?.originLonLat,
  }), [contextPlanes, siteGeometry, scene]);
  const contextRows = neighbours.rows;
  const obstacleTriangles = useMemo(() => combineTriangles(placed?.triangles, siteMesh.obstacles, neighbours.triangles), [placed, siteMesh, neighbours]);

  /**
   * A thinned copy for drawing only.
   *
   * A site export can carry hundreds of thousands of triangles; filling that
   * many paths per frame turns the time slider into a slideshow. Every stride-th
   * triangle is enough to read the shape, and the measurement below always uses
   * the full mesh - the picture is allowed to be approximate, the number is not.
   */
  /**
   * Whole-plant mode: the imported model contains the modules.
   *
   * Found rather than declared - the module faces are the triangles sitting at
   * the layout's own tilt and bearing, and if there are none the model is
   * surroundings and this stays off. In this mode the generated rows are not
   * drawn and the row formula is not used: every shadow, the row in front
   * included, comes out of the same ray cast, and the stored table is flagged
   * so the engine applies it INSTEAD of the row fraction rather than on top.
   */
  const plant = useMemo(() => {
    if (!placed || !scene) return null;
    const p = prepareWholePlant(placed.triangles, {
      tiltDeg: scene.surfaceTilt,
      azimuthDeg: scene.surfaceAzimuth,
      pitch: scene.pitch,
      target: 2500,
    });
    // A tree is not a plant.
    //
    // The test used to be `count > 0`: ONE triangle anywhere in the model
    // sitting within the classifier's 8 degrees of the collector normal, and
    // the panel switched to whole-plant mode -- generated rows gone, every
    // shading figure measured over those faces instead of over the array. An
    // 8 m tree with a rounded crown tripped it with 12 of its 768 crown
    // triangles and 1 m² of "collector", and the panel then reported a
    // two-row, 1 m² plant shaded 50%.
    //
    // So the model has to look like a plant, not merely contain a face at the
    // right angle: a real share of the array the project declares, or -- when
    // the project declares none, because the model IS the plant -- enough
    // collector to be one on its own. 200 m² is about 34 modules.
    if (!(p.count > 0)) return null;
    // The array the project declares, in collector area: one module's area is
    // the two edges the scene has already resolved, whichever way round the
    // modules are laid. `props` rather than a destructured name -- the module
    // count reaches this component as a prop and is consumed inside
    // useSceneModel, not in this scope.
    const moduleArea = scene.moduleWidth * (scene.collectorWidth / scene.verticalModules);
    const declared = (Number(props.totalModules) || 0) * (moduleArea > 0 ? moduleArea : 0);
    const enough = declared > 0
      ? p.area >= Math.min(0.2 * declared, 200)
      : (p.count >= 24 && p.area >= 20);
    return { ...p, stray: !enough };
  }, [placed, scene, props.totalModules]);

  const [wholePlant, setWholePlant] = useState(true);
  const plantMode = Boolean(plant) && !plant.stray && wholePlant;

  /**
   * The layout the model is built to, read back out of it.
   *
   * In whole-plant mode the typed spacing describes a plant that is already on
   * screen, and if the two disagree the surveyed one is the one that is true.
   * Shown rather than silently applied: a client-side mesh quietly rewriting
   * the saved layout - and with it the yield - is not something anyone could
   * audit afterwards. One button adopts it, and the layout's own Save commits.
   */
  const plantLayout = useMemo(() => {
    // A stray face or two is not a layout to adopt either: the panel offered
    // "2 rows · 2.83 m pitch · 1 m² of collector" measured off a tree's crown,
    // with a button to make it the project's layout.
    if (!plant || plant.stray || !scene) return null;
    // One module's area, taken from the two edges the scene already resolved -
    // their product is the same whichever way round the modules are laid.
    const area = scene.moduleWidth * (scene.collectorWidth / scene.verticalModules);
    return measureLayout(plant.collectors, {
      azimuthDeg: scene.surfaceAzimuth,
      moduleArea: area > 0 ? area : null,
    });
  }, [plant, scene]);

  const displayMesh = useMemo(() => {
    const drawingMesh = combineTriangles(placed?.triangles, siteMesh.obstacles, neighbours.solids);
    if (!drawingMesh) return null;
    const total = drawingMesh.length / 9;
    const limit = 2500;
    if (total <= limit) return drawingMesh;
    const stride = Math.ceil(total / limit);
    const out = new Float32Array(Math.ceil(total / stride) * 9);
    let o = 0;
    for (let t = 0; t < total; t += stride) {
      for (let k = 0; k < 9; k += 1) out[o + k] = drawingMesh[t * 9 + k];
      o += 9;
    }
    return out.subarray(0, o);
  }, [placed, siteMesh, neighbours]);

  const daylight = useMemo(() => {
    if (!scene || !Number.isFinite(scene.lat)) return null;
    return daylightWindow(2026, month, day, scene.lat, scene.lon, offsetHours);
  }, [scene, month, day, offsetHours]);

  // Keep the clock inside the day: a slider parked at 06:00 in December shows
  // an empty scene and looks broken.
  useEffect(() => {
    if (!daylight) return;
    setMinutes((m) =>
      clamp(m, Math.floor(daylight.sunrise) + 1, Math.ceil(daylight.sunset) - 1)
    );
  }, [daylight]);

  const sun = useMemo(() => {
    if (!scene || !Number.isFinite(scene.lat)) return null;
    const when = instantFromLocal(2026, month, day, minutes, offsetHours);
    return solarPosition(when, scene.lat, scene.lon);
  }, [scene, month, day, minutes, offsetHours]);

  const state = useMemo(
    () => (scene && sun ? shadingAt(scene, sun) : null),
    [scene, sun]
  );

  /**
   * A thinned set for the live readout.
   *
   * The full set is for the stored table, which is measured once. This one is
   * recomputed on every slider tick, so it trades a fraction of a percent of
   * resolution for a scene that still moves when you drag.
   */
  const plantLive = useMemo(() => {
    if (!plant) return null;
    const stride = Math.max(1, Math.ceil(plant.samples.length / 700));
    return plant.samples.filter((_, i) => i % stride === 0);
  }, [plant]);

  const plantNow = useMemo(() => {
    if (!plantMode || !sun || sun.zenith >= 90 || !plantLive) return null;
    return blockedFraction(
      combineTriangles(plant.occluders, siteMesh.obstacles, neighbours.triangles), plantLive, sunDirection(sun.zenith, sun.azimuth), plant.rows
    );
  }, [plantMode, plant, plantLive, sun, siteMesh, neighbours]);

  // The row quads, needed by three things now - the drawing, the live obstacle
  // reading, and the table builder - so they are built once here rather than
  // three times from three slightly different copies of the same code.
  // The neighbours' rows, once per layout rather than per frame. Empty when
  // this scene was not fitted to a boundary, since there is then no point on
  // the map to place them against.
  const geo = useMemo(
    () =>
      scene && state
        // The row's REAL length, so the modules drawn along it are the modules
        // the row has. It used to draw a representative slice - thirty modules
        // of fifty-eight - which kept the picture square and quietly answered a
        // different question than the one the caption was asking.
        ? buildRows(
            scene, state.rotation, onRoof || hasModuleMoves || individualEdit ? (scene.field?.rowsUsed || rowCount) : rowCount,
            scene.field?.rowLength || null, null,
            scene.field?.rowModules || null
          )
        : null,
    [scene, state, rowCount, onRoof, hasModuleMoves, individualEdit]
  );
  // Slots the plan holds no module in - taken out by hand around a chimney or
  // a skylight. A stable, sorted list, so a changed order is not a change.
  const excludedSlots = useMemo(() => (Array.isArray(props.excludedSlots) && props.excludedSlots.length
    ? [...new Set(props.excludedSlots)].sort() : []), [props.excludedSlots]);
  const hasExcludedSlots = excludedSlots.length > 0;
  const moduleFaces = useMemo(() => geo ? moduleQuads(geo.rows, scene.verticalModules, moduleOffsets, scene.terrain.groundZ, excludedSlots) : [],
    [geo, scene, moduleOffsets, excludedSlots]);
  const displayGeo = useMemo(() => layoutGeometry(geo, scene?.verticalModules, moduleOffsets, scene?.terrain.groundZ, individualEdit, excludedSlots),
    [geo, scene, moduleOffsets, individualEdit, excludedSlots]);
  // A measurement belongs to the receivers and the placement at the time of
  // the ray cast. Camera/time controls do not affect this key.
  const measurementKey = JSON.stringify([scene, placement, plantMode, contextPlanes, props.modulePositions, props.moduleOffsets, excludedSlots, sampleResolution]);
  const currentMeasurementKey = useRef(measurementKey);
  currentMeasurementKey.current = measurementKey;
  useEffect(() => {
    cancelRef.current = true;
    setTable(null);
    return () => { cancelRef.current = true; };
  }, [measurementKey, meshFile]);

  /**
   * What the panel reports.
   *
   * In whole-plant mode the row formula has nothing to say - the array it
   * describes is not the array on screen - so the fraction comes from the mesh
   * and the diode arithmetic is applied to that instead. Outside that mode this
   * is the analytic state, untouched.
   */
  const shownState = useMemo(() => {
    if (!state || !scene) return state;
    // Behind the ridge, or below the hillside the array stands on, there is no
    // direct beam - so there is no row shadow to report. The row formula knows
    // about neither, and left to itself it went on quoting a shaded fraction
    // for a sun that was not reaching the array.
    // Behind the plane of the modules there is no beam on the front either -
    // the transposition's cosine takes it to zero - so the chips must not go on
    // quoting a shaded fraction of a face the sun is behind.
    const front = geo?.rows?.[0]
      ? norm(cross(
          sub(geo.rows[0].low[1], geo.rows[0].low[0]),
          sub(geo.rows[0].high[0], geo.rows[0].low[0])
        ))
      : null;
    const outward = front ? (front.z < 0 ? mul(front, -1) : front) : null;
    const noBeam =
      !state.night && sun && sun.zenith < 90 &&
      ((outward && dot(outward, sunVector(sun.zenith, sun.azimuth)) <= 0) ||
        beamBlockedByHorizon(horizon, sun.azimuth, sun.elevation) ||
        (scene.terrain.slopeDir &&
          !sunAboveGround(
            sunVector(sun.zenith, sun.azimuth),
            scene.terrain.slopeDir,
            scene.terrain.slopeGrad
          )));
    if (noBeam) return { ...state, shaded: 0, bypassed: 0, extra: 0, total: 0 };
    if (!plantMode) return state;
    if (state.night || !plantNow) return { ...state, shaded: 0, bypassed: 0, extra: 0, total: 0 };
    return stateFromFraction(
      scene,
      state.rotation,
      plantNow.overall,
      Math.max(0, ...(plantNow.perRow || [0]))
    );
  }, [plantMode, state, plantNow, scene, sun, horizon, geo]);

  /**
   * The rows an obstacle is measured against: the derived field, never the
   * drawn slice.
   *
   * Shared by the live reading and by the stored table. Splitting them was the
   * bug hiding behind an innocent-looking dropdown - the chip said one thing,
   * the table another, and both moved when you changed how many rows were on
   * screen.
   */
  const measureRowsAt = useCallback(
    (rotation) => {
      if (!scene) return [];
      const f = scene.field;
      if (!f) return geo?.rows || [];
      const measured = buildRows(scene, rotation, scene.parcel ? scene.parcel.tables.length : hasModuleMoves ? f.rows : f.sampledRows, f.rowLength, f.rows, f.rowModules);
      return layoutGeometry(measured, scene.verticalModules, props.moduleOffsets, scene.terrain.groundZ, false, excludedSlots).rows;
    },
    [scene, geo, hasModuleMoves, props.moduleOffsets, excludedSlots]
  );
  const roofStatus = useMemo(() => {
    if (!scene || !state || !scene.field) return { outside: 0, intersecting: 0, belowRoof: 0 };
    const field = scene.field;
    const rows = buildRows(scene, state.rotation, field.rows,
      field.rowLength, field.rows, field.rowModules).rows;
    const faces = moduleQuads(rows, scene.verticalModules, props.moduleOffsets, scene.terrain.groundZ, excludedSlots);
    const keys = new Set(moduleQuads(rows, scene.verticalModules).map((q) => q.slot));
    return { ...checkRoofRows(siteGeometry, hasModuleMoves ? faces : rows),
      moduleOverlap: hasModuleMoves ? overlapCount(faces) : 0,
      invalidOffsets: Object.entries(props.moduleOffsets || {}).some(([key, offset]) => !keys.has(key) || !offset || ![offset.eastM, offset.northM].every(Number.isFinite)) };
  }, [scene, state, siteGeometry, hasModuleMoves, props.moduleOffsets, excludedSlots]);
  useEffect(() => { if (onRoofStatus) onRoofStatus(roofStatus); }, [onRoofStatus, roofStatus]);


  /**
   * The sun's track across the sky: the selected day, and the two solstices
   * behind it.
   *
   * A moving disc says where the sun is. The arcs say what the day is, and the
   * solstice pair says how much the year moves it - which is the difference
   * between "this pitch is fine" and "this pitch is fine in April". Sampled
   * every ten minutes and drawn as unit directions; the caller scales them onto
   * whatever radius the scene is using.
   */
  const sunArcs = useMemo(() => {
    if (!scene || !Number.isFinite(scene.lat)) return [];
    const arcFor = (m, d, accent) => {
      const points = [];
      const hours = [];
      for (let min = 0; min <= 1440; min += 10) {
        const s = solarPosition(
          instantFromLocal(2026, m, d, min, offsetHours), scene.lat, scene.lon
        );
        if (s.zenith >= 90) {
          // A gap rather than a line along the horizon: the sun is not there.
          points.push(null);
          continue;
        }
        points.push(sunVector(s.zenith, s.azimuth));
        if (min % 60 === 0) hours.push({ dir: sunVector(s.zenith, s.azimuth), hour: min / 60 });
      }
      return { key: `${m}-${d}`, points, hours: accent ? hours : [], accent };
    };
    const arcs = [arcFor(month, day, true)];
    // Context, not clutter: only when the chosen day is not already one of them.
    if (!(month === 12 && day === 21)) arcs.push(arcFor(12, 21, false));
    if (!(month === 6 && day === 21)) arcs.push(arcFor(6, 21, false));
    return arcs;
  }, [scene, month, day, offsetHours]);

  // Shaded fraction across the whole day, so the instant on screen can be
  // placed in its context: one bad hour and a bad morning are different
  // problems with different fixes.
  /**
   * The measured table for whole-plant mode: the one just built, or the one
   * stored with the project. Null means the model has not been measured yet,
   * and the things that need a whole day of shading say so instead of falling
   * back to a formula that describes a different array.
   */
  const plantTable = useMemo(() => {
    if (!plantMode) return null;
    const t = table || savedTable?.table || null;
    return t && t.includesRows ? t : null;
  }, [plantMode, table, savedTable]);

  const dayProfile = useMemo(() => {
    if (!scene || !daylight || !Number.isFinite(scene.lat)) return [];
    const out = [];
    const start = Math.max(0, Math.floor(daylight.sunrise));
    const end = Math.min(1440, Math.ceil(daylight.sunset));
    for (let m = start; m <= end; m += 5) {
      const s = solarPosition(
        instantFromLocal(2026, month, day, m, offsetHours),
        scene.lat,
        scene.lon
      );
      if (s.zenith >= 90) {
        out.push({ m, shaded: 0, loss: 0 });
        continue;
      }
      // Row shading is a share of the BEAM, so it is undefined while the
      // terrain is blocking the beam entirely.
      //
      // Without this the curve plotted a geometric row loss through the
      // horizon-blocked minutes at each end of the day -- 100% at sunrise,
      // because a sun on the horizon casts a shadow the length of the field --
      // and the caption underneath attributes the gap between the two traces to
      // the bypass diodes. During those minutes the diodes take nothing: the
      // ridge already took all of it, and the engine has zeroed the beam. The
      // hour is not "100% lost to row shading"; it is an hour with no direct
      // beam to lose, which is a different line in the loss diagram.
      if (horizon && beamBlockedByHorizon(horizon, s.azimuth, s.elevation)) {
        out.push({ m, shaded: null, loss: null, noBeam: true });
        continue;
      }
      const shaded = plantMode
        ? (plantTable ? lookupShadingTable(plantTable, s.azimuth, s.elevation) : null)
        : shadingAt(scene, s).shaded;
      if (shaded == null) return [];        // whole plant, not measured yet
      const st = plantMode
        ? stateFromFraction(scene, rowRotationAt(scene, s), shaded)
        : shadingAt(scene, s);
      out.push({ m, shaded: st.shaded, loss: st.total });
    }
    return out;
  }, [scene, daylight, month, day, offsetHours, plantMode, plantTable, horizon]);

  /**
   * The solar window: is the array clear between 09:00 and 15:00?
   *
   * A different question from "how much do I lose", and the one that decides a
   * permit rather than a yield. Answered from the same three mechanisms the
   * engine uses - terrain, the row in front, and an imported object - so the
   * verdict and the loss cannot disagree.
   */

  // --- animation ------------------------------------------------------------
  useEffect(() => {
    if (!playing || !daylight) return undefined;
    const id = window.setInterval(() => {
      setMinutes((m) => {
        const next = m + 4;
        return next > daylight.sunset ? Math.floor(daylight.sunrise) + 1 : next;
      });
    }, 60);
    return () => window.clearInterval(id);
  }, [playing, daylight]);

  // --- responsive canvas ----------------------------------------------------
  //
  // The canvas is sized from the element itself at draw time, not from a width
  // remembered in state. The panel starts life inside a hidden tab, where a
  // ResizeObserver reports nothing useful, so the first measurement was a
  // placeholder that then never got corrected - the scene drew into 640 px of a
  // 1300 px panel and left the rest blank. Measuring at the moment of drawing
  // cannot go stale; the observer's only job now is to ask for a redraw.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setResizeTick((t) => t + 1));
    ro.observe(el);
    // The height follows the window, which the wrapper's observer cannot see
    // when only the height changes (a browser going full screen, say).
    const onResize = () => setResizeTick((t) => t + 1);
    window.addEventListener("resize", onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
    // Keyed on `scene`: until the layout resolves there is no canvas to observe,
    // and an effect with [] deps would have given up permanently.
  }, [scene]);

  const editTargets = useMemo(() => {
    if (!scene || !geo || siteGeometryErrors(siteGeometry).length) return [];
    const targets = [{ id: "support", label: tr("Στέγη", "Roof"), mesh: siteMesh.roofMesh, editable: Boolean(siteGeometry?.frame && props.onSiteGeometryChange), gradient: { x: 0, y: 0 } },
      { id: "imported", mesh: placed?.triangles, editable: false }];
    neighbours.objects.forEach((o) => {
      if (!props.onContextLayoutChange) {
        targets.push({ id: `surface:${o.id}`, label: `${tr("Πλευρά Φ/Β", "PV surface")} ${o.label}`,
          mesh: o.mesh, editable: Boolean(o.id && o.frame && !o.previewOnly && props.onContextFrameChange), gradient: { x: 0, y: 0 } });
        return;
      }
      const add = (kind, mesh, gradient, objectId = null) => targets.push({
        id: JSON.stringify(["context", o.id, kind, objectId]), ownerId: o.id, kind, objectId,
        mesh, gradient, editable: Boolean(o.id && o.frame), label: `${tr("Πλευρά", "Surface")} ${o.label}`,
      });
      add("roof", o.roofMesh, { x: 0, y: 0 });
      if (individualEdit) o.modules.forEach((q) => add("module", rowsMesh([q]), o.gradient, q.slot));
      else add("array", o.arrayMesh, o.gradient);
      o.obstacles.forEach((obstacle) => add("obstacle", obstacle.mesh,
        obstacle.base === "roof" ? o.gradient : { x: 0, y: 0 }, obstacle.id));
    });
    if (!plantMode && individualEdit && props.onModuleOffsetsChange && !scene.tracker && !scene.parcel) {
      moduleFaces.forEach((q, i) => targets.push({ id: `module:${q.slot}`, label: `${tr("Πάνελ", "Module")} ${i + 1}`, mesh: rowsMesh([q]),
        editable: true, gradient: { x: scene.terrain.groundZ(1, 0), y: scene.terrain.groundZ(0, 1) } }));
    } else if (!plantMode) targets.push({ id: "array", label: tr("Ομάδα πάνελ", "Panel group"), mesh: rowsMesh(displayGeo.rows),
      editable: Boolean(props.onArrayOffsetChange) && !scene.parcel && !scene.tracker,
      gradient: { x: scene.terrain.groundZ(1, 0), y: scene.terrain.groundZ(0, 1) } });
    (siteGeometry?.obstacles || []).forEach((o, index) => targets.push({
      id: o.id, label: o.name || `${tr("Αντικείμενο", "Object")} ${index + 1}`,
      editable: Boolean(props.onSiteGeometryChange),
      mesh: buildSiteGeometry({ ...siteGeometry, obstacles: [o] }, scene.terrain.groundZ).obstacles,
      gradient: o.base === "roof" || !siteGeometry.roof
        ? { x: scene.terrain.groundZ(1, 0), y: scene.terrain.groundZ(0, 1) } : { x: 0, y: 0 },
    }));
    return targets;
  }, [scene, geo, displayGeo, moduleFaces, individualEdit, siteGeometry, siteMesh, neighbours, placed, plantMode, props.onArrayOffsetChange, props.onModuleOffsetsChange, props.onSiteGeometryChange, props.onContextFrameChange, props.onContextLayoutChange, greek]); // eslint-disable-line react-hooks/exhaustive-deps
  const canEdit = editTargets.some((t) => t.editable);
  const selectedTarget = editTargets.find((t) => t.id === selectedObject && t.editable);
  const clearanceGuides = useMemo(() => {
    if (!editMode || !individualEdit || !editingDrag) return [];
    const target = dragRef.current?.target;
    if (!target || !(target.kind === "module" || target.id.startsWith("module:"))) return [];
    const owner = target.ownerId || null;
    const key = (id, slot) => `${id || 'active'}:${slot}`;
    const modules = [
      ...moduleFaces.map(q => ({ ...q, id: key(null, q.slot) })),
      ...neighbours.objects.flatMap(o => o.modules.map(q => ({ ...q, id: key(o.id, q.slot) }))),
    ];
    const slots = owner ? [target.objectId] : (selectedModules.length ? selectedModules : [target.id.slice(7)]);
    const ids = new Set(slots.map(slot => key(owner, slot)));
    const obstacles = [];
    const collect = (geometry, id, shift = { x: 0, y: 0 }) => (geometry?.obstacles || []).forEach((o, i) => {
      if (o.kind === 'roof') return;
      const ring = o.kind === 'tree' ? Array.from({ length: 16 }, (_, n) => ({
        x: Number(o.eastM) + Number(o.widthM) / 2 * Math.cos(n * Math.PI / 8),
        y: Number(o.northM) + Number(o.depthM) / 2 * Math.sin(n * Math.PI / 8),
      })) : obstacleFootprint(o);
      obstacles.push({ id: key(id, o.id), label: o.name || tr(`Αντικείμενο ${i + 1}`, `Object ${i + 1}`),
        ring: ring.map(p => ({ x: p.x + shift.x, y: p.y + shift.y })) });
    });
    collect(siteGeometry, null);
    neighbours.objects.forEach(o => {
      const offset = sceneOffset({ siteGeometry }, o.plane);
      if (offset) collect(o.plane.siteGeometry, o.id, offset);
    });
    const roof = projectRoofFaces(contextPlanes, { siteGeometry }).find(f => f.owner === owner)?.ring || [];
    return moduleClearanceGuides({ selected: modules.filter(q => ids.has(q.id)), modules, obstacles, roof });
  }, [editMode, individualEdit, editingDrag, moduleFaces, selectedModules, neighbours.objects, siteGeometry, contextPlanes, greek]); // eslint-disable-line react-hooks/exhaustive-deps
  // Where the selected object stands, in metres east and north of the
  // surface's origin: read off the scene's corner while it is dragged,
  // instead of typed into boxes that said the same thing.
  const selectedPosition = useMemo(() => {
    const t = selectedTarget;
    if (!t) return null;
    const at = (east, north) => ({ east: Number(east) || 0, north: Number(north) || 0 });
    if (t.ownerId) {
      const plane = (contextPlanes || []).find((p) => p.id === t.ownerId);
      const layout = plane ? { ...plane.spacing, site_geometry: plane.siteGeometry } : null;
      if (!layout) return null;
      if (t.kind === "roof") return at(layout.site_geometry?.frame?.eastM, layout.site_geometry?.frame?.northM);
      if (t.kind === "obstacle") { const o = (layout.site_geometry?.obstacles || []).find((x) => x.id === t.objectId); return o ? at(o.eastM, o.northM) : null; }
      if (t.kind === "module") { const m = layout.module_offsets?.[t.objectId]; return at(m?.eastM, m?.northM); }
      return at(layout.array_offset_east_m, layout.array_offset_north_m);
    }
    if (t.id === "support") return at(siteGeometry?.frame?.eastM, siteGeometry?.frame?.northM);
    if (t.id === "array") return at(props.arrayOffsetEast, props.arrayOffsetNorth);
    if (t.id.startsWith("module:")) { const m = moduleOffsets?.[t.id.slice(7)]; return at(m?.eastM, m?.northM); }
    if (t.id.startsWith("surface:")) { const f = (contextPlanes || []).find((p) => p.id === t.id.slice(8))?.siteGeometry?.frame; return at(f?.eastM, f?.northM); }
    const o = (siteGeometry?.obstacles || []).find((x) => x.id === t.id);
    return o ? at(o.eastM, o.northM) : null;
  }, [selectedTarget, contextPlanes, siteGeometry, props.arrayOffsetEast, props.arrayOffsetNorth, moduleOffsets]);
  const editSnapshot = () => ({ geometry: siteGeometry,
    contexts: Object.fromEntries((contextPlanes || []).map((p) => [p.id, { ...p.spacing, site_geometry: p.siteGeometry }])),
    moduleOffsets: props.moduleOffsets || {}, selectedModules,
    frames: Object.fromEntries((contextPlanes || []).map((p) => [p.id, p.siteGeometry?.frame])),
    offset: { eastM: Number(props.arrayOffsetEast) || 0, northM: Number(props.arrayOffsetNorth) || 0 } });
  const restoreEdit = (edit) => {
    if (!edit) return;
    if (edit.undo) { edit.undo(); return; }
    const target = editTargets.find((t) => t.id === edit.id);
    if (target?.ownerId) props.onContextLayoutChange?.(target.ownerId, edit.before.contexts[target.ownerId]);
    else if (edit.id === "support") props.onSiteGeometryChange?.({ ...siteGeometry, frame: edit.before.geometry.frame });
    else if (edit.id === "array") props.onArrayOffsetChange?.(edit.before.offset);
    else if (edit.id.startsWith("module:")) {
      props.onModuleOffsetsChange?.(edit.before.moduleOffsets);
    }
    else if (edit.id.startsWith("surface:")) props.onContextFrameChange?.(edit.id.slice(8), edit.before.frames[edit.id.slice(8)]);
    else {
      const original = edit.before.geometry?.obstacles?.find((o) => o.id === edit.id);
      if (original) props.onSiteGeometryChange?.({ ...siteGeometry, obstacles: siteGeometry.obstacles.map((o) =>
        o.id === edit.id ? { ...o, eastM: original.eastM, northM: original.northM } : o) });
    }
  };
  const moveObject = (id, before, delta, snap = false, view = null, preview = false) => {
    const round = (n) => Math.round(n * 100) / 100;
    const target = editTargets.find((t) => t.id === id);
    if (target?.ownerId) {
      const layout = before.contexts[target.ownerId], geometry = layout.site_geometry;
      const shift = (value) => ({ ...value, eastM: round((Number(value.eastM) || 0) + delta.x),
        northM: round((Number(value.northM) || 0) + delta.y) });
      let patch;
      if (target.kind === "roof") patch = { site_geometry: { ...geometry, frame: shift(geometry.frame) } };
      else if (target.kind === "obstacle") patch = { site_geometry: { ...geometry,
        obstacles: geometry.obstacles.map((o) => o.id === target.objectId ? shift(o) : o) } };
      else if (target.kind === "module") patch = { module_offsets: {
        ...layout.module_offsets, [target.objectId]: shift(layout.module_offsets?.[target.objectId] || {}),
      }, ...(layout.module_array ? { module_array: { ...layout.module_array,
        individualOffsets: { ...layout.module_array.individualOffsets,
          [target.objectId]: shift(layout.module_array.individualOffsets?.[target.objectId] || {}) } } } : {}) };
      else patch = { array_offset_east_m: round((Number(layout.array_offset_east_m) || 0) + delta.x),
        array_offset_north_m: round((Number(layout.array_offset_north_m) || 0) + delta.y) };
      props.onContextLayoutChange?.(target.ownerId, { ...layout, ...patch });
    } else if (id === "support") props.onSiteGeometryChange?.({ ...before.geometry, frame: {
      ...before.geometry.frame, eastM: round(Number(before.geometry.frame.eastM) + delta.x),
      northM: round(Number(before.geometry.frame.northM) + delta.y),
    } });
    else if (id === "array") props.onArrayOffsetChange?.({
      eastM: round(before.offset.eastM + delta.x), northM: round(before.offset.northM + delta.y),
    });
    else if (id.startsWith("module:")) {
      const selected = before.selectedModules.includes(id.slice(7)) ? before.selectedModules : [id.slice(7)];
      let movement = delta;
      if (snap && snapEnabled) {
        // The other modules do not move during a drag: their faces are built
        // once for it, not on every pointer move.
        const faces = before.snapFaces || (before.snapFaces = moduleQuads(geo.rows, scene.verticalModules, before.moduleOffsets, scene.terrain.groundZ, before.excludedSlots ?? excludedSlots));
        const result = snapModuleDelta(faces, selected, delta, {
          // About twenty screen pixels, and never less than ten centimetres:
          // close enough to be meant, wide enough to be caught.
          threshold: Math.max(0.1, Math.min(0.4, 20 / (view?.scale || 50))), gap: Math.max(0, Number(snapGap) || 0) / 100,
          groundZ: scene.terrain.groundZ,
          // Snapping aligns panels; roof/obstacle validity is reported by the
          // layout check below. An already invalid roof position must not make
          // the alignment control appear broken while the panel is being moved.
        });
        movement = result.delta;
        setSnapInfo(result.snapped ? { slots: selected, target: result.target, kind: result.kind } : null);
        setEditHint(result.snapped
          ? (result.kind === "slot"
            ? tr("✓ Κούμπωσε: ακριβώς δίπλα στο γειτονικό πάνελ, στην ίδια γραμμή (πράσινο περίγραμμα). Alt: χωρίς κούμπωμα.", "✓ Snapped: right beside the neighbouring module, in line with it (green outline). Alt: no snapping.")
            : tr("✓ Κούμπωσε στην ακμή του γειτονικού πάνελ (πράσινο περίγραμμα)· πλησιάστε το άκρο του για να μπει και στη γραμμή του.", "✓ Snapped to the neighbouring module's edge (green outline); move towards its end to line up with it too."))
          : tr("Ελεύθερη θέση: φέρτε το πάνελ κοντά σε ένα γειτονικό για να κουμπώσει.", "Free position: bring the module near a neighbour to snap."));
      } else setSnapInfo(null);
      const offsets = { ...before.moduleOffsets };
      selected.forEach((key) => {
        const old = before.moduleOffsets[key] || {};
        const eastM = Math.round(((old.eastM || 0) + movement.x) * 10000) / 10000;
        const northM = Math.round(((old.northM || 0) + movement.y) * 10000) / 10000;
        if (Math.abs(eastM) + Math.abs(northM) < 0.0001 && !old.rotationDeg && !old.elevationM) delete offsets[key];
        else offsets[key] = { ...old, eastM, northM };
      });
      if (preview && dragRef.current) {
        dragRef.current.pendingOffsets = offsets;
        setDragOffsets(offsets);
      } else props.onModuleOffsetsChange?.(offsets);
    } else if (id.startsWith("surface:")) {
      const key = id.slice(8), frame = before.frames[key];
      props.onContextFrameChange?.(key, { ...frame, eastM: round(Number(frame.eastM) + delta.x), northM: round(Number(frame.northM) + delta.y) });
    } else props.onSiteGeometryChange?.({ ...before.geometry,
      obstacles: before.geometry.obstacles.map((o) => o.id === id
        ? { ...o, eastM: round(Number(o.eastM) + delta.x), northM: round(Number(o.northM) + delta.y) } : o),
    });
  };

  // A press and release on another face of the shared scene, with no drag,
  // makes it the face being edited; the page swaps the form underneath.
  const pressRef = useRef(null);
  const selectPlaneAtRef = useRef(null);
  selectPlaneAtRef.current = (point) => {
    const view = projectionRef.current;
    if (!view || !props.onSelectPlane) return;
    const target = pickSceneObject(view, point, editTargets.map((t) => ({ ...t, editable: true })));
    const ownerId = target?.ownerId || (target?.id?.startsWith("surface:") ? target.id.slice(8) : null);
    if (ownerId) props.onSelectPlane(ownerId);
  };

  // --- orbit / move / zoom --------------------------------------------------
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    pressRef.current = { x: e.clientX, y: e.clientY };
    if (editMode) {
      const view = projectionRef.current;
      if (!view) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const target = pickSceneObject(view, { x: e.clientX - rect.left, y: e.clientY - rect.top }, editTargets);
      setSelectedObject(target?.id || "");
      setSnapInfo(null);
      if (!target) {
        if (!e.shiftKey) setSelectedModules([]);
        setEditHint("");
        dragRef.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture?.(e.pointerId);
        return;
      }
      let selection = selectedModules;
      if (target.id.startsWith("module:")) {
        const key = target.id.slice(7);
        selection = e.shiftKey ? (selection.includes(key) ? selection.filter((k) => k !== key) : [...selection, key])
          : selection.includes(key) ? selection : [key];
        setSelectedModules(selection);
        if (e.shiftKey) { setSelectedObject(selection.length ? `module:${selection[selection.length - 1]}` : ""); e.currentTarget.focus(); return; }
      } else setSelectedModules([]);
      if (!sceneDragDelta(view, 0, 0, target.gradient)) {
        setEditHint(tr("Επιλέξτε 2D για μετακίνηση σε αυτή την επιφάνεια.", "Choose 2D to move on this surface."));
        return;
      }
      setEditHint("");
      setPlaying(false);
      dragRef.current = { x: e.clientX, y: e.clientY, view, target, before: { ...editSnapshot(), selectedModules: selection }, moved: false };
      setEditingDrag(true);
      e.currentTarget.focus();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      return;
    }
    dragRef.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (d.target) {
      const delta = sceneDragDelta(d.view, dx, dy, d.target.gradient);
      if (delta && (d.moved || Math.hypot(dx, dy) > 3)) {
        d.moved = true;
        moveObject(d.target.id, d.before, delta, !e.altKey, d.view, true);
      }
      return;
    }
    dragRef.current = { x: e.clientX, y: e.clientY };
    if (viewMode === "2d") {
      setCamera((c) => ({ ...c, el: 90, panX: (c.panX || 0) + dx, panY: (c.panY || 0) + dy }));
      return;
    }
    setCamera((c) => ({
      ...c,
      az: (c.az + dx * 0.4 + 360) % 360,
      // From the horizon to straight overhead, and no further. Edge-on at 0
      // is how you read a pitch and a ground clearance; straight down at 90 is
      // how you read a layout. Below 0 the camera is underground, looking up
      // through the terrain at the backs of the modules with the shadows on the
      // far side of a ground plane that hides them - a view of the site that
      // cannot be had and that shows none of what this picture is for.
      el: clamp(c.el + dy * 0.3, 0, 90),
    }));
  };
  const onPointerUp = useCallback((e) => {
    const d = dragRef.current;
    if (d?.pendingOffsets) commitOffsetsRef.current?.(d.pendingOffsets);
    setDragOffsets(null);
    if (d?.moved) setLastEdit({ id: d.target.id, before: d.before });
    const press = pressRef.current;
    pressRef.current = null;
    if (press && e?.currentTarget?.getBoundingClientRect && !d?.moved
      && Math.hypot(e.clientX - press.x, e.clientY - press.y) < 3) {
      const rect = e.currentTarget.getBoundingClientRect();
      selectPlaneAtRef.current?.({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
    dragRef.current = null;
    setEditingDrag(false);
    if (e?.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);
  const cancelMove = () => {
    const d = dragRef.current;
    setDragOffsets(null);
    // A previewed module move never reached the layout, so there is nothing
    // to restore there; anything else did, and is put back.
    if (d?.target && d.moved && !d.pendingOffsets) restoreEdit({ id: d.target.id, before: d.before });
    dragRef.current = null;
    setEditingDrag(false);
  };
  // Take the selected modules out of the plan. The slots stay in the grid so
  // every other module keeps its coordinates and its offset. The parent keeps
  // the original string links until an electrical proposal is explicitly applied.
  const removeSelectedModules = () => {
    if (!individualEdit || !selectedModules.length || !props.onEditPlanModules) return;
    const edit = props.onEditPlanModules({ type: "remove", slots: selectedModules });
    if (edit) setLastEdit({ id: "module:remove", undo: edit.undo });
    setSelectedModules([]);
  };
  // Add exactly one panel through the same atomic edit as the 2D plan.
  const addModule = () => {
    const rowModules = scene?.field?.rowModules;
    if (!individualEdit || !props.onEditPlanModules || !Array.isArray(rowModules) || !rowModules.length) return;
    const picked = selectedModules.length ? Number(selectedModules[selectedModules.length - 1].split(":")[0]) : rowModules.length;
    const row = Number.isInteger(picked) && picked >= 1 && picked <= rowModules.length ? picked : rowModules.length;
    const edit = props.onEditPlanModules({ type: "add", row });
    if (edit) { setLastEdit({ id: "module:add", undo: edit.undo }); setSelectedModules([edit.slot]); }
  };
  const onEditKeyDown = (e) => {
    if (!editMode) return;
    if (e.key === "Escape") { e.stopPropagation(); cancelMove(); return; }
    if ((e.key === "Delete" || e.key === "Backspace") && individualEdit && selectedModules.length && !dragRef.current) {
      e.preventDefault(); removeSelectedModules(); return;
    }
    if (!selectedTarget || dragRef.current) return;
    const step = e.shiftKey ? 1 : 0.1;
    const delta = { ArrowLeft: { x: -step, y: 0 }, ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: step }, ArrowDown: { x: 0, y: -step } }[e.key];
    if (!delta) return;
    e.preventDefault();
    const before = editSnapshot();
    setLastEdit({ id: selectedObject, before });
    moveObject(selectedObject, before, delta);
  };

  /**
   * Put the camera where the shadows are visible.
   *
   * A shadow falls away from the sun, so looking from anywhere near the sun's
   * own bearing puts every shadow behind the thing casting it - correct, and
   * useless. Standing about a quarter turn off the sun keeps the rows face-on
   * and lays the shadows out sideways where they can be read. Offered as a
   * button rather than forced, because once someone has orbited deliberately
   * the view is theirs.
   */
  const resetView = useCallback(() => {
    const from = sun && sun.zenith < 90 ? (sun.azimuth + 100) % 360 : 205;
    setCamera({ az: viewMode === "2d" ? 180 : from, el: viewMode === "2d" ? 90 : 26, zoom: 1, panX: 0, panY: 0 });
  }, [sun, viewMode]);
  // Wheel zoom, as a NON-PASSIVE native listener.
  //
  // React registers wheel handlers as passive, so preventDefault() inside one is
  // ignored: the wheel zoomed the scene AND scrolled the page at the same time,
  // which is what made the page jump about. A native listener registered with
  // {passive: false} can actually cancel the scroll. Only while the pointer is
  // over the viewer after a click - leaving it releases the wheel again.
  const hasWheelScene = Boolean(scene);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    let wheelActive = false;
    const releaseWheel = bindClickWheelZoom(el, (active) => { wheelActive = active; });
    const onWheel = (e) => {
      if (!wheelActive) return;
      e.preventDefault();
      // Zoom about the pointer, not about the middle of the frame. Everything
      // the projection draws is (world - boxCentre) * scale away from an
      // anchor, and scale is proportional to zoom - so holding the point under
      // the cursor still is just moving the anchor by the same ratio. Without
      // this, zooming in on a far row walked it off the edge of the canvas and
      // the only way back was to zoom out again.
      const box = (canvasRef.current || el).getBoundingClientRect();
      const mx = e.clientX - box.left;
      const my = e.clientY - box.top;
      setCamera((c) => {
        const zoom = clamp(c.zoom * (e.deltaY > 0 ? 0.92 : 1.08), 0.2, 20);
        const k = zoom / c.zoom;
        const ax = box.width / 2 + (c.panX || 0);
        const ay = box.height / 2 + (c.panY || 0);
        return {
          ...c,
          zoom,
          // Clamped, so a scene cannot be flung somewhere it can never be
          // scrolled back from - there is no pan gesture to recover with.
          panX: clamp(mx - (mx - ax) * k - box.width / 2, -box.width, box.width),
          panY: clamp(my - (my - ay) * k - box.height / 2, -box.height, box.height),
        };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => { el.removeEventListener("wheel", onWheel); releaseWheel(); };
    // Same reason as the observer above: the element does not exist on the
    // first render, so this has to re-run once it does.
  }, [hasWheelScene]);

  // --- the imported model ---------------------------------------------------
  const loadFile = useCallback(async (file) => {
    if (!file) return;
    setMeshError("");
    setTable(null);
    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseSTL(buffer);
      const count = parsed.triangles.length / 9;
      if (count === 0) throw new Error("The file contains no triangles.");
      const loaded = { name: file.name, triangles: parsed.triangles, format: parsed.format };
      setMeshFile(loaded);
      // Best effort, and deliberately not awaited: an import must not wait on a
      // disk quota, and a refusal to store costs the picture next time, not now.
      saveMesh(projectId, loaded);
      // A first guess at the units from the size. A CAD file in millimetres
      // read as metres is a kilometre-high building, which is obvious in the
      // picture but easier to just not do.
      const b = boundsOf(parsed.triangles);
      const span = Math.max(b.size[0], b.size[1], b.size[2]);
      setPlacement((prev) => ({
        ...prev,
        units: span > 2000 ? "mm" : span > 200 ? "cm" : prev.units,
      }));
      if (count > 200000) {
        setMeshError(
          `${count.toLocaleString("en-GB")} triangles: this will measure, but slowly. ` +
          "A simplified model of the same shape gives the same shadows in a fraction of the time."
        );
      }
    } catch (err) {
      setMeshFile(null);
      setMeshError(err.message || "That file could not be read as an STL.");
    }
  }, [projectId]);

  const onDropFile = useCallback(
    (e) => {
      e.preventDefault();
      loadFile(e.dataTransfer?.files?.[0]);
    },
    [loadFile]
  );

  /**
   * The model back on screen after a reload, from this machine's own cache.
   *
   * Only when there is nothing loaded: a user who has just dropped a file is
   * looking at what they chose, and a cache entry arriving late and replacing
   * it under them would be a bug with no visible cause. Runs once per project
   * rather than on every render for the same reason.
   */
  const restoredFor = useRef(null);
  useEffect(() => {
    if (!projectId || restoredFor.current === projectId) return;
    restoredFor.current = projectId;
    let live = true;
    loadMesh(projectId).then((cached) => {
      // Nothing cached, the panel closed while we waited, or the user got there
      // first with a file of their own.
      if (!live || !cached) return;
      setMeshFile((current) => current || cached);
    });
    return () => { live = false; };
  }, [projectId]);

  const clearMesh = useCallback(() => {
    setMeshFile(null);
    setTable(null);
    setMeshError("");
    // The cache follows the measurement out. Leaving the mesh behind would put
    // the object back on screen at the next reload with nothing applying it,
    // which is the same confusion the other way round.
    deleteMesh(projectId);
    // Clearing the model clears what the engine is using too. Leaving a stored
    // table behind after the object it describes is gone would be the worst of
    // both: an invisible loss with nothing on screen to explain it.
    if (onObstacleTableChange) onObstacleTableChange(null);
  }, [onObstacleTableChange, projectId]);

  const measureObstacle = useCallback(async () => {
    if (props.moduleMappingValid === false) return;
    if ((!obstacleTriangles && !hasModuleMoves && !onMeasureAll) || !geo || neighbours.missing.length) return;
    setAllError("");
    // Through the layout, which calculates from the layout as it stands and
    // keeps the result for its Save: every face of a shared scene, or the one
    // face -- rows alone included. A single face measured as a whole imported
    // plant (the model's own module faces) is measured here.
    if (onMeasureAll && !(plantMode && measureAllCount <= 1)) {
      cancelRef.current = false;
      setAllError("");
      setTableProgress(0);
      try {
        await onMeasureAll({ placement: meshFile ? placement : null,
          onProgress: (p) => setTableProgress(p), shouldCancel: () => cancelRef.current });
      } catch (err) {
        // Stopped from the calculation's own window: nothing went wrong.
        if (!cancelRef.current && !err?.cancelled) setAllError(scenePreparationMessage(err, greek));
      } finally {
        setTableProgress(null);
      }
      return;
    }
    const startedFor = currentMeasurementKey.current;
    cancelRef.current = false;
    setTableProgress(0);
    try {
      // Measured across the REAL field, never across the drawn slice. The Rows
      // dropdown is a drawing choice; letting it decide how much of the array an
      // obstacle covers would put a viewing preference into the yield.
      const rowsFor = scene.tracker
        ? (az, el) =>
            measureRowsAt(
              rowRotationAt(scene, { zenith: 90 - el, azimuth: az, elevation: el })
            )
        : null;
      const physicalRows = buildRows(scene, state.rotation, scene.parcel ? scene.parcel.tables.length : (scene.field?.rowsUsed || rowCount),
        scene.field?.rowLength || null, null, scene.field?.rowModules || null).rows;
      const perModule = !plantMode && !scene.tracker && props.modulePositions?.length > 0;
      const result = await buildShadingTableAsync({
        triangles: perModule ? combineTriangles(obstacleTriangles, rowsMesh(moduleQuads(physicalRows, scene.verticalModules, props.moduleOffsets, scene.terrain.groundZ, excludedSlots))) : obstacleTriangles,
        // Whole-plant mode measures the module faces found in the model, and
        // the occluders include those same faces - that is what makes the row
        // in front part of the same cast. The table then carries includesRows,
        // and the engine replaces the row fraction with it instead of adding.
        ...(plantMode
          ? { explicitSamples: plant.samples, rowCount: plant.rows, rows: [] }
          : { rows: measureRowsAt(state.rotation), rowsFor }),
        ...(perModule ? {
          explicitSamples: moduleSamples(physicalRows, props.modulePositions, scene.verticalModules, sampleResolution, props.moduleOffsets, scene.terrain.groundZ),
          rowCount: physicalRows.length, moduleMetadata: props.modulePositions, includesRows: true,
          ...(sampleResolution === 8 ? { azStep: 5, azCount: 72, elStep: 2.5, elCount: 37 } : {}),
        } : {}),
        latitude: scene.lat,
        onProgress: (p) => setTableProgress(p),
        shouldCancel: () => cancelRef.current,
      });
      if (!cancelRef.current && startedFor === currentMeasurementKey.current) {
        // One click: what is calculated goes to the layout (the effect below),
        // which keeps it with its draft until its Save stores both together,
        // so the run has it and never has to work it out in the browser.
        storeOnMeasureRef.current = true;
        setTable(result);
      }
    } catch (err) {
      if (!cancelRef.current) setAllError(scenePreparationMessage(err, greek));
    } finally {
      setTableProgress(null);
    }
  }, [obstacleTriangles, geo, scene, state, measureRowsAt, plantMode, plant, neighbours, props.modulePositions, props.moduleOffsets, props.moduleMappingValid, excludedSlots, hasModuleMoves, sampleResolution, rowCount, onMeasureAll, measureAllCount, meshFile, placement, greek]);
  useEffect(() => {
    if (onPlacementChange) onPlacementChange(meshFile ? placement : null);
  }, [onPlacementChange, meshFile, placement]);
  // A finished calculation goes to the layout it was made on.
  useEffect(() => {
    if (!storeOnMeasureRef.current || !table) return;
    storeOnMeasureRef.current = false;
    onObstacleTableChange?.({
      table,
      ...(meshFile ? { placement } : {}),
      siteGeometry,
      sceneVersion: 2,
      contextIds: (contextPlanes || []).map((p) => p.id),
      source: {
        name: meshFile ? meshFile.name : "Shared project scene",
        triangles: (obstacleTriangles?.length || 0) / 9,
        format: meshFile ? meshFile.format : "parametric",
      },
      measuredAt: new Date().toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  // --- draw -----------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !scene || !sun || !state) return;
    const dpr = window.devicePixelRatio || 1;
    // Measured now, from the element, so a hidden-then-shown panel is drawn at
    // the width it actually has.
    const W = Math.max(320, Math.round(wrapRef.current?.clientWidth || 640));
    const H = canvasHeight();
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = "100%";
    canvas.style.height = `${H}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;                                   // no 2D context: nothing to draw on
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    projectionRef.current = drawShadingScene(ctx, {
      W, H, isDark, scene, sun,
      state: shownState || state,
      // The generated rows are removed entirely in whole-plant mode. Drawing
      // both would be two arrays on one site, and the one built from the
      // formula is the one that is not there.
      geo: plantMode ? { ...geo, rows: [] } : displayGeo,
      camera, rowCount: geo.rows.length, showSegments, moduleTopology: lineTopology, sunArcs: editMode ? [] : sunArcs, displayMesh, horizon,
      frame: editMode || onRoof ? "edit" : "scene",
      // The far horizon is drawn whenever the run applies it (the switch under
      // the scene); a second, picture-only switch said the same thing twice.
      showSun: !editMode, showHorizon: Boolean(horizon),
      horizonLabel: greek ? "Μακρινός ορίζοντας (PVGIS)" : "Far horizon (PVGIS)",
      compassLabels: greek ? ["Β", "Α", "Ν", "Δ"] : ["N", "E", "S", "W"],
      editGrid: editMode, surfaceType: props.surfaceType, siteRadius: props.siteRadiusM,
      workspace: editMode || onRoof ? sceneWorkspace.current : null,
      supportMesh: siteMesh.roofMesh, roofBoundary: siteMesh.boundary, solidMesh: combineTriangles(siteMesh.solidObstacles, neighbours.closedSolids),
      groundShadowMesh: obstacleTriangles,
      contextRows, shadowMesh: displayGeo.individualModules ? combineTriangles(obstacleTriangles, rowsMesh(displayGeo.rows)) : obstacleTriangles,
      projection: dragRef.current?.view,
      clearanceGuides,
      clearanceLocale: greek ? "el-GR" : "en-GB",
      selectionMesh: editMode ? (individualEdit && selectedModules.length ? rowsMesh(moduleFaces.filter((q) => selectedModules.includes(q.slot))) : selectedTarget?.mesh) : null,
      snapGuide: editMode && individualEdit && snapInfo
        ? moduleFaces.filter((q) => snapInfo.slots.includes(q.slot) || q.slot === snapInfo.target)
          .map((q) => ({ points: [q.low[0], q.low[1], q.high[1], q.high[0]], target: q.slot === snapInfo.target }))
        : null,
    });
  }, [scene, sun, state, shownState, plantMode, geo, camera, resizeTick, isDark, snapInfo,
      showSegments, lineTopology, sunArcs, displayMesh, rowCount, horizon, contextRows, siteMesh, neighbours.closedSolids, obstacleTriangles, onRoof,
      greek, editMode, props.surfaceType, props.siteRadiusM, selectedTarget, editingDrag, displayGeo, individualEdit, selectedModules, moduleFaces, hasModuleMoves, clearanceGuides]);

  // --- off-screen snapshots ------------------------------------------------
  //
  // The same drawing routine as the live view, pointed at a canvas nobody sees,
  // at a date and time the caller names. Always drawn on the light palette:
  // it is going onto paper. The camera is the user's own, so the printed views
  // are the angle they were looking from.
  useEffect(() => {
    if (!snapshotRef) return undefined;
    snapshotRef.current = ({ month: m, day: d, minutes: mins, width = 960, height = 540,
                             format = "png", quality = 0.85,
                             frame = "scene", typeScale = 1 } = {}) => {
      if (!scene || !Number.isFinite(scene.lat)) return null;
      const shotMonth = m ?? month;
      const shotDay = d ?? day;
      // The offset AT THE DATE BEING DRAWN, not at the date the slider happens
      // to be parked on. `offsetHours` is memoised against month/day, so with
      // the slider on the solstice every summer snapshot was placed an hour
      // early - the June view of a sheet rendered from a December scene was
      // drawn at 11:00 and captioned 12:00.
      const shotOffset = offsetForDate(shotMonth, shotDay);
      const when = instantFromLocal(2026, shotMonth, shotDay, mins ?? minutes, shotOffset);
      const sunAt = solarPosition(when, scene.lat, scene.lon);
      if (!sunAt || sunAt.zenith >= 90) return null;         // night: nothing to draw
      const stateAt = shadingAt(scene, sunAt);
      const baseGeoAt = buildRows(
        scene, stateAt.rotation, onRoof || hasModuleMoves ? (scene.field?.rowsUsed || rowCount) : rowCount,
        scene.field?.rowLength || null, null, scene.field?.rowModules || null
      );
      const geoAt = layoutGeometry(baseGeoAt, scene.verticalModules, props.moduleOffsets, scene.terrain.groundZ, false, excludedSlots);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      drawShadingScene(ctx, {
        W: width, H: height, isDark: false, scene, sun: sunAt, state: stateAt,
        geo: plantMode ? { ...geoAt, rows: [] } : geoAt,
        groundShadowMesh: obstacleTriangles,
        contextRows, shadowMesh: hasModuleMoves ? combineTriangles(obstacleTriangles, rowsMesh(geoAt.rows)) : obstacleTriangles,
        // The camera angle is the user's own, so a printed view is the angle
        // they were looking from -- but not their zoom or pan: a sheet must not
        // depend on where the panel happened to be scrolled to. The framing
        // fits the layout on its own.
        // NOTHING about the printed camera is the user's own -- not the zoom,
        // not the pan, and not the bearing either.
        //
        // The bearing was, on the reasoning that a printed view should be the
        // side they were looking from. But the subject of these four views is
        // the SHADOW, and a shadow is only visible from the sun's side of the
        // rows: a panel left facing north-east printed four views of a plant
        // with no shadows in it, because every shadow was behind the row that
        // cast it. The sheet cannot depend on where the panel was left.
        //
        // So it looks from in front of the modules, turned a little off their
        // axis: the faces read as faces, the aisles stay open, and whatever
        // falls into them is in the picture. High enough, too -- at 24 degrees
        // the near row hides the aisle behind it.
        camera: frame === "array"
          ? { az: printedBearing(scene, camera), el: Math.max(camera.el, 38),
              zoom: 1, panX: 0, panY: 0 }
          : camera,
        rowCount: baseGeoAt.rows.length, showSegments, moduleTopology: lineTopology, sunArcs: [], displayMesh,
        horizon: frame === "array" ? null : horizon,
        frame, typeScale, surfaceType: props.surfaceType, siteRadius: props.siteRadiusM,
        supportMesh: siteMesh.roofMesh, roofBoundary: siteMesh.boundary, solidMesh: combineTriangles(siteMesh.solidObstacles, neighbours.closedSolids),
      });
      let snapshotFraction = stateAt.shaded;
      const commonMeasurement = !plantMode && !scene.tracker && props.modulePositions?.length > 0;
      if (commonMeasurement) {
        const allRows = buildRows(scene, stateAt.rotation, scene.parcel ? scene.parcel.tables.length : scene.field.rowsUsed,
          scene.field.rowLength, null, scene.field.rowModules).rows;
        snapshotFraction = blockedFraction(combineTriangles(obstacleTriangles, rowsMesh(moduleQuads(allRows, scene.verticalModules, props.moduleOffsets, scene.terrain.groundZ, excludedSlots))),
          moduleSamples(allRows, props.modulePositions, scene.verticalModules, sampleResolution, props.moduleOffsets, scene.terrain.groundZ),
          sunDirection(sunAt.zenith, sunAt.azimuth), allRows.length).overall;
      }
      return {
        shadingScope: commonMeasurement ? "shared_scene" : "rows",
        // JPEG when the picture is going to be STORED with the project, where a
        // PNG of a shaded scene is five times the size for no visible gain.
        image: format === "jpeg" ? canvas.toDataURL("image/jpeg", quality) : canvas.toDataURL("image/png"),
        shadedPct: Number.isFinite(snapshotFraction) ? 100 * snapshotFraction : null,
        sunElevation: 90 - sunAt.zenith,
      };
    };
    return () => { snapshotRef.current = null; };
  }, [snapshotRef, scene, month, day, minutes, offsetForDate, rowCount, onRoof, plantMode,
      camera, showSegments, lineTopology, displayMesh, horizon, contextRows, siteMesh, neighbours.closedSolids, obstacleTriangles, props.modulePositions, props.moduleOffsets, hasModuleMoves, sampleResolution,
      props.surfaceType, props.siteRadiusM, excludedSlots]);

  if (!scene) {
    return (
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
        <Typography variant="body2" color="text.secondary">
          Set the pitch and the number of stacked modules to see the array in 3D.
          The view is drawn from the saved layout, not from assumed dimensions.
        </Typography>
      </Box>
    );
  }

  const sunUp = sun && sun.zenith < 90;

  return (
    <Stack
      spacing={1.1}
      sx={fullscreen ? {
        position: "fixed",
        inset: 0,
        zIndex: (t) => t.zIndex.modal,
        bgcolor: "background.paper",
        p: 2,
        overflowY: "auto",
      } : undefined}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {tr("Κοινή σκηνή και σκιάσεις σε 3D", onRoof ? "Shared roof scene and shading in 3D" : "Row shading in 3D")}
        </Typography>
        <MaybeTooltip
          arrow
          title={tr(
            (timeZoneLabel ? `Οι ώρες είναι τοπικές για τη ζώνη ${timeZoneLabel}, με τη θερινή ώρα όπου ισχύει. ` : "") +
            "Η σκηνή δείχνει τη θέση του ήλιου και τις σκιές για την επιλεγμένη ημέρα και ώρα, με βάση τα πάνελ, τη στέγη ή το έδαφος και τα εμπόδια που έχουν οριστεί. Ο μακρινός ορίζοντας συνυπολογίζεται όταν είναι ενεργός. Πρόκειται για στιγμιαία γεωμετρική προεπισκόπηση, όχι για ετήσια απώλεια παραγωγής. Τα όρια ηλεκτρικών τμημάτων εμφανίζονται από το αντίστοιχο switch και δεν δείχνουν ενεργοποίηση διόδων ή απώλεια ισχύος.",
            (timeZoneLabel ? `Times are local to ${timeZoneLabel}, including daylight saving where applicable. ` : "") +
            "The scene shows the sun position and shadows for the selected date and time, based on the modules, roof or terrain, and defined obstacles. The far horizon is included when enabled. This is an instantaneous geometric preview, not an annual production loss. Electrical section boundaries are shown by their switch and do not indicate diode conduction or power loss."
          )}
        >
          <Chip size="small" variant="outlined" label={tr("Πώς διαβάζεται", "How to read this")} sx={{ cursor: "help" }} />
        </MaybeTooltip>
        {!props.detailedMode && <Stack direction="row" alignItems="center" spacing={0.25}>
          <TextField select size="small" label={tr("Δειγματοληψία σκίασης", "Shading sampling")} value={sampleResolution}
            disabled={tableProgress != null || plantMode}
            onChange={(e) => { const resolution = Number(e.target.value); setSampleResolution(resolution); props.onSamplesPerModuleChange?.(resolution * resolution); }}
            sx={{ width: 178, "& .MuiInputBase-input": { py: 0.75, fontSize: 13 } }}>
            <MenuItem value={4}>{tr("16 σημεία · 4 × 4", "16 points · 4 × 4")}</MenuItem>
            <MenuItem value={8}>{tr("64 σημεία · 8 × 8", "64 points · 8 × 8")}</MenuItem>
          </TextField>
          <MaybeTooltip arrow title={tr("Σημεία ελέγχου σκιάς σε κάθε πάνελ. Τα 16 σημεία είναι ταχύτερα. Τα 64 αποτυπώνουν λεπτομερέστερα μικρές σκιές και χρησιμοποιούν πυκνότερες θέσεις ήλιου, με μεγαλύτερο χρόνο υπολογισμού. Αποθηκεύστε τη διάταξη για να εφαρμοστεί η επιλογή στην επόμενη προσομοίωση. Δεν αλλάζει η γεωμετρία ή η ισχύς των πάνελ.", "Shadow test points on each module. 16 points are faster. 64 resolve smaller shadows and use a finer sun-position grid, taking longer to calculate. Save the layout to use this setting in the next simulation. Module geometry and capacity are unchanged.")}>
            <IconButton size="small" aria-label={tr("Πληροφορίες δειγματοληψίας", "Sampling information")}><InfoOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
          </MaybeTooltip>
        </Stack>}
        <Box sx={{ flex: 1 }} />
        <MaybeTooltip arrow title={fullscreen ? tr("Επιστροφή στη φόρμα (Esc)", "Back to the form (Esc)") : tr("Προβολή σε όλο το παράθυρο", "Fill the window")}>
          <Button size="small" onClick={() => setFullscreen((v) => !v)}>
            {fullscreen ? tr("Έξοδος από πλήρη οθόνη", "Exit full screen") : tr("Πλήρης οθόνη", "Full screen")}
          </Button>
        </MaybeTooltip>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={String(dateDoy)}
          onChange={(e, v) => {
            if (!v) return;
            setDateDoy(Number(v));
          }}
        >
          {DATE_PRESETS.map((p) => (
            <ToggleButton key={p.key} value={String(toDayOfYear(p.month, p.day))} sx={{ px: 1.2 }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {/* A number, not a menu. A menu offers four arbitrary choices and then
            covers itself with its own tooltip; typing 14 is what someone with a
            14-row field actually wants. The explanation moved to the helper
            text, where it cannot sit on top of anything. */}
        {!onRoof && <TextField
          size="small"
          type="number"
          label="Rows drawn"
          value={hasModuleMoves || individualEdit ? (scene.field?.rowsUsed || rowsInput) : rowsInput}
          disabled={hasModuleMoves || individualEdit}
          onChange={(e) => {
            const raw = e.target.value;
            setRowsInput(raw);
            const n = Number(raw);
            if (Number.isInteger(n) && n >= minDrawnRows && n <= maxDrawnRows) {
              setRowCount(n);
            }
          }}
          onBlur={() => {
            // Whatever was left in the box, land on something drawable.
            const n = Number(rowsInput);
            const fixed = Number.isFinite(n)
              ? clamp(Math.round(n), minDrawnRows, maxDrawnRows)
              : suggestedRows;
            setRowCount(fixed);
            setRowsInput(String(fixed));
          }}
          inputProps={{ min: minDrawnRows, max: maxDrawnRows, step: 1 }}
          helperText={hasModuleMoves || individualEdit ? tr("Εμφανίζονται όλα τα πάνελ", "All modules are shown") : " "}
          sx={{ width: 132, "& .MuiFormHelperText-root": { mx: 0, fontSize: 10 } }}
        />}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <ToggleButtonGroup exclusive size="small" value={viewMode} aria-label={tr("Προβολή σκηνής", "Scene view")}
          onChange={(_, mode) => {
            if (!mode) return;
            if (mode === viewMode) return;
            cancelMove();
            if (viewMode === "3d") last3dElevation.current = camera.el;
            setViewMode(mode);
            setCamera((c) => ({ ...c,
              viewAnchor: c.viewAnchor ?? projectionRef.current?.viewAnchor,
              el: mode === "2d" ? 90 : last3dElevation.current }));
          }}>
          <ToggleButton value="2d">2D</ToggleButton>
          <ToggleButton value="3d">3D</ToggleButton>
        </ToggleButtonGroup>
        {viewMode === "2d" && <>
          <IconButton size="small" aria-label={tr("Περιστροφή προβολής αριστερά", "Rotate view left")}
            onClick={() => setCamera((c) => ({ ...c, az: (c.az + 345) % 360, el: 90 }))}><RotateLeftIcon /></IconButton>
          <IconButton size="small" aria-label={tr("Περιστροφή προβολής δεξιά", "Rotate view right")}
            onClick={() => setCamera((c) => ({ ...c, az: (c.az + 15) % 360, el: 90 }))}><RotateRightIcon /></IconButton>
        </>}
      </Stack>

      {(canEdit || editMode) && <Stack spacing={0.75}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Button size="small" variant={editMode ? "contained" : "outlined"} onClick={() => {
            cancelMove(); setEditMode(!editMode); setEditHint(""); setPlaying(false);
          }}>{editMode ? tr("Τέλος επεξεργασίας", "Finish editing") : tr("Επεξεργασία θέσεων", "Edit positions")}</Button>
          {editMode && props.onModuleOffsetsChange && !plantMode && !scene.tracker && !scene.parcel && <TextField
            id="scene-panel-move-mode" select size="small" label={tr("Μετακίνηση πάνελ", "Panel movement")}
            value={individualEdit ? "individual" : "group"} onChange={(event) => {
              cancelMove(); setIndividualEdit(event.target.value === "individual"); setSelectedObject(""); setSelectedModules([]); setEditHint("");
            }} sx={{ minWidth: 210 }}>
            <MenuItem value="group">{tr("Όλη η ομάδα", "Whole group")}</MenuItem>
            <MenuItem value="individual">{tr("Μεμονωμένα πάνελ", "Individual modules")}</MenuItem>
          </TextField>}

          {editMode && <Button size="small" disabled={!lastEdit} onClick={() => { restoreEdit(lastEdit); setLastEdit(null); }}>
            {tr("Αναίρεση μετακίνησης", "Undo move")}</Button>}
        </Stack>
        {editMode && individualEdit && <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <FormControlLabel control={<Switch size="small" checked={snapEnabled} onChange={(e) => setSnapEnabled(e.target.checked)} />}
            label={tr("Κούμπωμα σε γειτονικά πάνελ", "Snap to neighbouring modules")} />
          <TextField size="small" type="number" label={tr("Οριζόντιο διάκενο (cm)", "Horizontal gap (cm)")} value={snapGap}
            onChange={(e) => setSnapGap(e.target.value === "" ? "" : Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
            inputProps={{ min: 0, max: 100, step: 0.5 }} sx={{ width: 180 }} />
          <Typography variant="caption">{selectedModules.length} {tr("επιλεγμένα", "selected")}</Typography>
          {/* Taking a module out, and putting one in. The plan is allowed to
              differ from the electrical design after this - that is the point
              - and the layout says so beside the Save and refuses the run
              until the strings are fitted to the plan or the plan put back. */}
          {props.onEditPlanModules && <Button size="small" color="error" disabled={!selectedModules.length}
            onClick={removeSelectedModules}>
            {tr("Αφαίρεση επιλεγμένων", "Remove selected")}{selectedModules.length ? ` (${selectedModules.length})` : ""}</Button>}
          {props.onEditPlanModules && scene?.field?.rowModules && <Button size="small" onClick={addModule}>
            {tr("Προσθήκη πάνελ", "Add module")}</Button>}
          <Button size="small" disabled={!hasModuleMoves} onClick={() => {
            setLastEdit({ id: "module:reset", before: editSnapshot() }); props.onModuleOffsetsChange?.({}); setEditHint("");
          }}>{tr("Επαναφορά θέσεων πάνελ", "Reset module positions")}</Button>
        </Stack>}
        {editMode && individualEdit && hasExcludedSlots && props.moduleMappingValid === false && props.missingModuleCount > 0 && <Typography variant="caption" color="warning.main">{tr(
          `${props.missingModuleCount} πάνελ λείπουν από τα strings. Συμφιλιώστε την κάτοψη με τον ηλεκτρολογικό σχεδιασμό πριν τον υπολογισμό.`,
          `${props.missingModuleCount} module(s) missing from strings. Reconcile the plan and electrical design before simulating.`)}</Typography>}
        {editMode && individualEdit && <Typography variant="caption" color="text.secondary">{tr(
          "Κλικ και σύρσιμο ενός πάνελ. Shift + κλικ για επιλογή πολλών και κοινή μετακίνηση. Alt κατά το σύρσιμο απενεργοποιεί προσωρινά το κούμπωμα. Η αντιστοίχιση στα strings διατηρείται.",
          "Click and drag a module. Shift + click selects several to move together. Hold Alt while dragging to bypass snapping. String assignments are preserved.")}</Typography>}
        {Boolean(roofStatus.moduleOverlap) && <Alert severity="warning">{tr("Υπάρχουν επικαλυπτόμενα πάνελ. Μετακινήστε τα πριν αποθηκεύσετε.", "Modules overlap. Move them before saving.")}</Alert>}
        {editMode && <Typography variant="caption" color="text.secondary">
          {editHint || (viewMode === "2d" ? tr("Σύρετε αντικείμενο για μετακίνηση ή το κενό για μετακίνηση προβολής. Περιστροφή με τα βέλη δίπλα στο 2D. Ροδέλα: zoom. Αποθηκεύστε με «Αποθήκευση διάταξης».", "Drag an object to move it or empty space to pan. Rotate using the arrows beside 2D. Scroll to zoom. Save with Save layout.") : tr("Σύρετε με αριστερό πάνω σε αντικείμενο για μετακίνηση ή στο κενό για περιστροφή. Ροδέλα: zoom. Βέλη: 10 cm · Shift + βέλος: 1 m · Esc: ακύρωση. Αποθηκεύστε με «Αποθήκευση διάταξης».",
            "Left drag an object to move it, or empty space to rotate. Scroll to zoom. Arrow keys: 10 cm · Shift + arrow: 1 m · Esc: cancel. Save with Save layout."))}
        </Typography>}
      </Stack>}

      <Box
        ref={wrapRef}
        tabIndex={editMode ? 0 : undefined}
        role={editMode ? "region" : undefined}
        aria-label={tr("Επεξεργασία σκηνής", "Scene editor")}
        data-testid="scene-view"
        sx={{
          position: "relative",
          flexShrink: 0,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          cursor: editingDrag ? "grabbing" : "grab",
          touchAction: "none",
        }}
        onPointerDown={onPointerDown}
        onContextMenu={(e) => e.preventDefault()}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={cancelMove}
        onLostPointerCapture={onPointerUp}
        onKeyDown={onEditKeyDown}
        onDoubleClick={editMode ? undefined : resetView}
      >
        <canvas ref={canvasRef} style={{ display: "block", width: "100%" }} />
        {clearanceGuides.length > 0 && <Typography variant="caption" role="status"
          sx={{ position: "absolute", left: 8, bottom: 28, px: 0.7, borderRadius: 1,
            bgcolor: "background.paper", color: "text.primary", pointerEvents: "none" }}>
          {tr("Αποστάσεις σε κάτοψη · ακμή με ακμή", "Plan distances · edge to edge")}
        </Typography>}
        <Typography
          variant="caption"
          sx={{
            position: "absolute", left: 8, top: 6, color: "text.secondary",
            bgcolor: "background.paper", opacity: 0.85, px: 0.7, borderRadius: 1,
            pointerEvents: "none",
          }}
        >
          {MONTH_NAMES[month - 1]} {day} · {clockLabel(minutes)} UTC
          {offsetHours >= 0 ? "+" : ""}
          {offsetHours}
          {sun && sun.elevation > 0 &&
            beamBlockedByHorizon(horizon, sun.azimuth, sun.elevation)
            ? " · sun behind the far horizon, no direct beam"
            : ""}
        </Typography>
        {editMode && selectedTarget && selectedPosition && <Typography
          variant="caption"
          data-testid="scene-position"
          sx={{
            position: "absolute", right: 8, bottom: 28, color: "text.primary",
            bgcolor: "background.paper", opacity: 0.9, px: 0.7, borderRadius: 1,
            fontVariantNumeric: "tabular-nums", pointerEvents: "none",
          }}
        >
          {selectedTarget.label || tr("Επιλεγμένο", "Selected")}: x {selectedPosition.east >= 0 ? "+" : ""}{selectedPosition.east.toFixed(2)} m · y {selectedPosition.north >= 0 ? "+" : ""}{selectedPosition.north.toFixed(2)} m
        </Typography>}
        <Typography
          variant="caption"
          sx={{
            position: "absolute", right: 8, bottom: 6, color: "text.secondary",
            bgcolor: "background.paper", opacity: 0.8, px: 0.7, borderRadius: 1,
            pointerEvents: "none",
          }}
        >
          {viewMode === "2d" ? <>{tr("2D · σύρσιμο στο κενό: μετακίνηση προβολής · βέλη: περιστροφή · ροδέλα: zoom · διπλό κλικ: επαναφορά", "2D · drag empty space to pan · rotation buttons · scroll to zoom · double-click to reset")}
            {!onRoof && <> · {rowCount} of the field's {scene.field?.rows ?? rowCount} rows · {scene.field ? describeRows(scene.field) : `${geo?.modulesAlong ?? 0} modules`}</>}
          </> : editMode ? tr("Σύρσιμο: αντικείμενο → μετακίνηση · κενό → περιστροφή · ροδέλα → zoom", "Drag: object → move · empty space → rotate · scroll → zoom") : onRoof ? tr(
            `Σύρετε για περιστροφή · κύλιση για μεγέθυνση · ενεργή πλευρά: ${Number(props.totalModules) || 0} πάνελ`,
            `Drag to orbit · scroll to zoom · active surface: ${Number(props.totalModules) || 0} modules`
          ) : greek ? `Σύρετε για περιστροφή · κύλιση για μεγέθυνση · διπλό κλικ για επαναφορά · ${rowCount} από ${scene.field?.rows ?? rowCount} σειρές` : <>drag to orbit · scroll to zoom at the pointer · double-click to reset ·{" "}
          {rowCount} of the field's{" "}
          {scene.field ? `${scene.field.rows} ` : ""}rows ·{" "}
          {scene.field
            ? describeRows(scene.field)
            : `${geo?.modulesAlong ?? 0} modules`}</>}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary">
        {tr("Κλικ μέσα στη σκηνή για zoom με τη ροδέλα. Βγάλτε τον δείκτη από το πλαίσιο ή πατήστε Esc για κύλιση της σελίδας.", "Click inside the scene to zoom with the wheel. Move the pointer outside or press Esc to scroll the page.")}
      </Typography>

      {scene.roof && !siteMesh.roofMesh && <Typography variant="caption" color="text.secondary">
        {tr("Ορίστε έγκυρες διαστάσεις κτιρίου και στέγης για να εμφανιστεί η πραγματική επιφάνεια στήριξης στη 3D σκηνή.",
          "Enter valid building and roof dimensions to show the actual support surface in the 3D scene.")}
      </Typography>}

      <Stack direction="row" spacing={1.2} alignItems="center">
        <Typography variant="caption" sx={{ minWidth: 34, color: "text.secondary" }}>
          {tr("Ώρα", "Time")}
        </Typography>
        <Slider
          size="small"
          value={minutes}
          min={daylight ? Math.floor(daylight.sunrise) : 0}
          max={daylight ? Math.ceil(daylight.sunset) : 1440}
          step={1}
          onChange={(e, v) => {
            setPlaying(false);
            setMinutes(Number(v));
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={clockLabel}
          sx={{ flex: 1, height: 6, "& .MuiSlider-thumb": { width: 16, height: 16 } }}
        />
        <IconButton
          size="small"
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? tr("Παύση", "Pause") : tr("Αναπαραγωγή ημέρας", "Play the day")}
        >
          {playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
        </IconButton>
        <Typography variant="caption" sx={{ minWidth: 92, color: "text.secondary" }}>
          {daylight
            ? `${clockLabel(daylight.sunrise)}–${clockLabel(daylight.sunset)}`
            : "no sunrise"}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.2} alignItems="center">
        <Typography variant="caption" sx={{ minWidth: 34, color: "text.secondary" }}>
          {tr("Ημερομηνία", "Date")}
        </Typography>
        <Slider
          size="small"
          value={dateDoy}
          min={1}
          max={365}
          step={1}
          onChange={(e, v) => setDateDoy(Number(v))}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => {
            const d = fromDayOfYear(v);
            return `${d.day} ${MONTH_NAMES[d.month - 1]}`;
          }}
          sx={{ flex: 1, height: 6, "& .MuiSlider-thumb": { width: 16, height: 16 } }}
        />
      </Stack>

      {/* The day, at a glance. One shaded hour at dawn and a shaded morning are
          different problems, and a single instant cannot tell them apart. */}
      {hasModuleMoves && <Alert severity="info">{tr("Η διάταξη έχει ανεξάρτητες θέσεις πάνελ. Υπολογίστε και εφαρμόστε τις σκιάσεις της σκηνής πριν από την προσομοίωση.", "This layout has individual module positions. Calculate and apply scene shading before simulation.")}</Alert>}
      {!onRoof && props.parcelOutline?.frame_origin_xy && <Typography variant="caption" color="text.secondary">{tr(
        "Οι σκιάσεις αυτής της χωροταξίας υπολογίζονται από τις πραγματικές θέσεις των σειρών με «Υπολογισμός σκιάσεων».",
        "Use Calculate shading to evaluate this layout from the actual row positions.")}</Typography>}
      {!hasModuleMoves && !(props.parcelOutline?.frame_origin_xy && !onRoof) && dayProfile.length > 1 && (
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.3 }}>
            {greek ? <><b>Σκίαση μεταξύ σειρών · {day}/{month}</b>. Πορτοκαλί: απώλεια άμεσης ακτινοβολίας με την προσεγγιστική επίδραση διόδων. Μπλε διακεκομμένη: γεωμετρικά σκιασμένη επιφάνεια. Οι γκρι ζώνες δεν έχουν άμεση ακτινοβολία. Η καμπύλη αφορά τις σειρές· οι μετρήσεις της κοινής σκηνής αναφέρονται χωριστά. Κλικ για επιλογή ώρας.</> : <><b>Losses across {MONTH_NAMES[month - 1]} {day}</b>, sunrise to sunset — solid orange is
            the beam lost, dashed blue the area actually shadowed. The gap between them is what the
            bypass diodes take. Shaded bands are minutes with no direct beam at all — the terrain
            has taken it, so row shading has nothing to act on. Click to jump to an hour.</>}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "stretch", gap: 0.5 }}>
            {/* A real axis. The two lines used to share a scale normalised to
                whatever the day's maximum happened to be, so a 6% day and a 60%
                day drew the same picture. Fixed 0-100% means days can be
                compared against each other by eye, which is the whole point of
                looking at three dates. */}
            <Box sx={{ width: 26, position: "relative", height: 64, flexShrink: 0 }}>
              {[100, 50, 0].map((v, i) => (
                <Typography
                  key={v}
                  variant="caption"
                  sx={{
                    position: "absolute", right: 0, fontSize: 9, lineHeight: 1,
                    color: "text.secondary",
                    top: `${i * 50}%`,
                    transform: i === 0 ? "none" : i === 2 ? "translateY(-100%)" : "translateY(-50%)",
                  }}
                >
                  {v}%
                </Typography>
              ))}
            </Box>
            <Box sx={{ flex: 1 }}>
              <svg
                viewBox="0 0 100 24"
                preserveAspectRatio="none"
                style={{ width: "100%", height: 64, display: "block", cursor: "pointer" }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const f = clamp((e.clientX - rect.left) / rect.width, 0, 1);
                  const first = dayProfile[0].m;
                  const last = dayProfile[dayProfile.length - 1].m;
                  setPlaying(false);
                  setMinutes(Math.round(first + f * (last - first)));
                }}
              >
                <rect x="0" y="0" width="100" height="24" fill={isDark ? "#12202f" : "#f3f7fc"} rx="1" />
                {[0.25, 0.5, 0.75].map((g) => (
                  <line
                    key={g} x1="0" x2="100" y1={24 * g} y2={24 * g}
                    stroke={theme.palette.divider} strokeWidth="0.5"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {/* Minutes the terrain has taken entirely, banded rather than
                    plotted. Row shading is a share of the BEAM, so it has no
                    value while there is no beam -- and drawing the geometric
                    figure there put a 100% spike at each end of a winter day
                    under a caption crediting the gap to bypass diodes, which
                    take nothing when the ridge has already taken everything. */}
                {(() => {
                  const spans = [];
                  let open = null;
                  dayProfile.forEach((p, i) => {
                    if (p.noBeam && open == null) open = i;
                    if (!p.noBeam && open != null) {
                      spans.push([open, i]);
                      open = null;
                    }
                  });
                  if (open != null) spans.push([open, dayProfile.length - 1]);
                  const step = 100 / Math.max(1, dayProfile.length - 1);
                  return spans.map(([a, b]) => (
                    <rect
                      key={`nb-${a}`}
                      x={(a * step).toFixed(2)}
                      y="0"
                      width={Math.max(0.4, (b - a) * step).toFixed(2)}
                      height="24"
                      fill={theme.palette.text.primary}
                      opacity={isDark ? 0.16 : 0.08}
                    />
                  ));
                })()}
                {["loss", "shaded"].map((key) => (
                  // Broken into runs so a banded gap is a GAP, not a straight
                  // line drawn across it.
                  <g key={key}>
                    {(() => {
                      const runs = [];
                      let run = [];
                      dayProfile.forEach((p, i) => {
                        if (p[key] == null) {
                          if (run.length > 1) runs.push(run);
                          run = [];
                          return;
                        }
                        const x = (100 * i) / (dayProfile.length - 1);
                        const y = 24 * (1 - p[key]);
                        run.push(`${x.toFixed(2)},${y.toFixed(2)}`);
                      });
                      if (run.length > 1) runs.push(run);
                      return runs.map((points, idx) => (
                        <polyline
                          key={idx}
                          points={points.join(" ")}
                          fill="none"
                          stroke={key === "loss"
                            ? theme.palette.warning.main
                            : theme.palette.primary.main}
                          strokeWidth={key === "loss" ? "1.4" : "1"}
                          strokeDasharray={key === "loss" ? undefined : "3 2"}
                          vectorEffect="non-scaling-stroke"
                        />
                      ));
                    })()}
                  </g>
                ))}
                {(() => {
                  const first = dayProfile[0].m;
                  const last = dayProfile[dayProfile.length - 1].m;
                  const x = (100 * (minutes - first)) / Math.max(1, last - first);
                  return (
                    <line
                      x1={x} x2={x} y1="0" y2="24"
                      stroke={theme.palette.text.primary} strokeWidth="1"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })()}
              </svg>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.1 }}>
                <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>
                  {clockLabel(dayProfile[0].m)} sunrise
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>
                  {daylight ? clockLabel(daylight.solarNoon) : ""} solar noon
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>
                  sunset {clockLabel(dayProfile[dayProfile.length - 1].m)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
        <MaybeTooltip
          arrow
          title={tr("Η θέση του ήλιου υπολογίζεται στον περιηγητή ώστε ο δείκτης χρόνου να ανταποκρίνεται αμέσως. Η προσομοίωση χρησιμοποιεί pvlib· οι τιμές συμφωνούν με απόκλιση έως 0,005°.",
            "Computed in your browser, so the slider is instant. The simulation uses pvlib; the two agree to 0.005 degrees.")}
        >
          <Chip size="small" variant="outlined" sx={{ cursor: "help" }}
            label={sunUp
              ? tr(`Ήλιος: ύψος ${fmtDeg(sun.elevation)}, αζιμούθιο ${fmtDeg(sun.azimuth)} · NOAA/Meeus`, `Sun ${fmtDeg(sun.elevation)} up, ${fmtDeg(sun.azimuth)} az · NOAA/Meeus`)
              : tr("Ο ήλιος κάτω από τον ορίζοντα", "Sun below the horizon")} />
        </MaybeTooltip>
        {horizonAvailable && (
          <MaybeTooltip
            arrow
            describeChild
            title={tr(
              `Μακρινή σκίαση από το προφίλ εδάφους του έργου (${horizonDescription(horizonAvailable)}), που ελήφθη από το PVGIS. Αν απενεργοποιηθεί, αφαιρείται από την εικόνα και από τον υπολογισμό της άμεσης ακτινοβολίας. Το προφίλ παραμένει αποθηκευμένο για επανενεργοποίηση.`,
              `Far shading from the terrain profile stored with this project (${describeHorizon(horizonAvailable)}), ` +
              "fetched from PVGIS. Switching it off removes the ridge from the view AND stops the " +
              "simulation zeroing the beam behind it - the profile is kept, so it can be switched " +
              "back on without asking PVGIS again. Turn it off to see what the site would yield on " +
              "an open plain, or when you know the profile is wrong."
            )}
          >
            <FormControlLabel
              sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
              control={
                <Switch
                  size="small"
                  checked={horizonOn}
                  disabled={!onHorizonEnabledChange}
                  onChange={(e) => {
                    if (onHorizonEnabledChange) onHorizonEnabledChange(e.target.checked);
                  }}
                />
              }
              label={tr("Μακρινός ορίζοντας", "Far horizon")}
            />
          </MaybeTooltip>
        )}
        {!plantMode && <MaybeTooltip arrow describeChild title={lineTopology
          ? tr("Εμφανίζει μόνο τα όρια των ηλεκτρικών ομάδων από τη διάταξη του έργου. Οι κυψέλες παραμένουν πάντα ορατές. Δεν αλλάζει τους υπολογισμούς.",
            "Shows only the electrical-group boundaries from the project's layout. Cells remain visible at all times. It does not change calculations.")
          : tr("Εμφανίζει τα όρια τμημάτων της τυπικής μεθόδου, βάσει τύπου κυψελών και διόδων. Οι κυψέλες παραμένουν ορατές.", "Shows standard-method section boundaries from cell type and diode count. Cells remain visible.")}>
          <FormControlLabel sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
            control={<Switch size="small" checked={showSegments} disabled={Boolean(props.moduleTopology && !lineTopology)}
              onChange={e => setShowSegments(e.target.checked)} />}
            label={tr("Όρια ηλεκτρικών τμημάτων", "Electrical section boundaries")} />
        </MaybeTooltip>}
      </Stack>
      {(plantMode || (plant && !plant.stray && !wholePlant)) && <Stack direction="row" spacing={1}>
        {plantMode && (
          <MaybeTooltip
            arrow
            title={tr(
              `Όλα τα πλαίσια του εισαγόμενου μοντέλου (${plant.count} επιφάνειες, ${plant.rows} σειρές) συμμετέχουν στη μέτρηση σκίασης. Οι σκιές προέρχονται από το μοντέλο, χωρίς να προστίθεται ξανά ο τύπος σκίασης σειρών. Αφαιρέστε την επιλογή για να χρησιμοποιηθεί το αρχείο μόνο ως περιβάλλον.`,
              `${describePlant(plant, { tiltDeg: scene.surfaceTilt, azimuthDeg: scene.surfaceAzimuth })}, ` +
              `over ${plant.rows} row${plant.rows === 1 ? "" : "s"}. Every shadow, the row in ` +
              "front included, comes from the model - the row formula is not used, so nothing " +
              "is counted twice. Remove the chip to treat the file as surroundings only."
            )}
          >
            <Chip
              size="small"
              color="info"
              sx={{ cursor: "help" }}
              onDelete={() => setWholePlant(false)}
              label={tr(`Όλη η εγκατάσταση από το μοντέλο · ${plant.count} επιφάνειες πλαισίων`, `Whole plant from the model · ${plant.count} module faces`)}
            />
          </MaybeTooltip>
        )}
        {plant && !plant.stray && !wholePlant && (
          <Chip
            size="small"
            variant="outlined"
            onClick={() => setWholePlant(true)}
            sx={{ cursor: "pointer" }}
            label={tr("Το μοντέλο περιέχει πλαίσια — χρήση ως πλήρης εγκατάσταση", "Model has modules in it — use it as the whole plant")}
          />
        )}
      </Stack>}

      {!fullscreen && <Divider sx={{ my: 0.5 }} />}

      {/* Buildings, trees and pylons are the shading everyone actually worries
          about, and the row model cannot see them at all. Importing the site
          model measures them - and, once saved, hands the measurement to the
          simulation so the yield stops pretending the object is not there. */}
      <Stack spacing={1} sx={fullscreen ? { display: "none" } : undefined}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {tr("Μοντέλο τοποθεσίας (STL)", "Site model (STL)")}
          </Typography>
          <MaybeTooltip
            arrow
            title={tr(
              `Μονάδες σε μέτρα, με τον άξονα Z προς τα πάνω. Η αρχή του μοντέλου τοποθετείται στο κέντρο της διάταξης. Σε στέγη, τοποθετήστε την αρχή στο επίπεδο της στέγης ώστε το κτίριο να εκτείνεται προς αρνητικό Z. Αν το STL περιέχει τα Φ/Β πλαίσια, η σκίαση υπολογίζεται από όλη την εγκατάσταση χωρίς πρόσθετο τύπο σκίασης σειρών. Αλλιώς αντιμετωπίζεται ως περιβάλλον. Επιφάνειες με κλίση ${Number(scene.surfaceTilt).toFixed(0)}° και αζιμούθιο ${Number(scene.surfaceAzimuth).toFixed(0)}° αναγνωρίζονται ως πλαίσια.`,
              "Metres, Z up, placed around the array origin.\n\n" +
              "For a roof array put the model's origin ON THE ROOF at the centre of the " +
              "array, so the building goes down into negative Z, and set the model to sit " +
              "on the roof below. Dropped to the ground instead, a building modelled from " +
              "its street level lifts its roof a whole storey above the modules.\n\n" +
              "With the PV rows in it, the whole plant is measured from the model and the row " +
              "formula is not used.\n\n" +
              "Without them, it is treated as surroundings and combined with the row model.\n\n" +
              `Detected, not declared: faces at ${Number(scene.surfaceTilt).toFixed(0)}\u00b0 / ` +
              `${Number(scene.surfaceAzimuth).toFixed(0)}\u00b0 are read as modules.`
            )}
          >
            <Chip size="small" variant="outlined" label={tr("Τι να εισαγάγετε", "What to import")} sx={{ cursor: "help" }} />
          </MaybeTooltip>
          <Box sx={{ flex: 1 }} />
          {/* Reachable whenever something is IN FORCE, not only while the mesh
              happens to be on screen. After a refresh the file is gone and the
              measurement is not, and this was the only control that could take
              it out of the simulation -- so the one state where a user most
              needs it was the one state where it was not rendered. Getting rid
              of an applied loss then meant finding the original STL again. */}
          {(meshFile || savedTable) && (
            <Button size="small" color="inherit" onClick={clearMesh}>
              Remove
            </Button>
          )}
        </Stack>

        {!meshFile ? (
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropFile}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              p: 2, borderRadius: 2, textAlign: "center", cursor: "pointer",
              border: "1px dashed", borderColor: "divider",
              "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {tr("Σύρετε εδώ ένα αρχείο STL — περιβάλλον ή ολόκληρη εγκατάσταση", "Drop an .STL here — surroundings, or the whole plant")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {tr("ή κάντε κλικ για επιλογή αρχείου — δυαδικό ή ASCII, διαβάζεται τοπικά στον περιηγητή και δεν μεταφορτώνεται", "or click to choose a file — binary or ASCII, read in your browser and never uploaded")}
            </Typography>
          </Box>
        ) : null}

        {/* A measurement without its model.
            The file is read in the browser and never sent anywhere, so a
            refresh loses it while the measurement it produced stays with the
            project and stays in force. Saying that only inside a tooltip on a
            chip meant it read as lost work, and the honest reading of an empty
            drop-zone is that the object is no longer in the simulation - which
            is the opposite of what is happening. */}
        {!meshFile && savedTable && !siteMesh.obstacles && savedTable.sceneVersion !== 2 ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <b>{savedTable.source?.name || "An imported model"}</b> was measured
            {savedTable.measuredAt
              ? ` on ${new Date(savedTable.measuredAt).toLocaleDateString("en-GB")}`
              : ""}
            {savedTable.table
              ? ` — worst case ${pct(Math.max(0, ...savedTable.table.values.filter((v) => v >= 0)))} of the array`
              : ""}
            . The simulation is applying it.
            {/* Where, in words, because the placement fields belong to the mesh
                and are not on screen without it. A figure that moves the yield
                and cannot be inspected is not one a user can defend. */}
            {savedTable.placement
              ? ` Placed ${Math.abs(savedTable.placement.north || 0).toFixed(0)} m`
                + ` ${(savedTable.placement.north || 0) < 0 ? "south" : "north"}`
                + ((savedTable.placement.east || 0)
                    ? ` and ${Math.abs(savedTable.placement.east).toFixed(0)} m`
                      + ` ${savedTable.placement.east < 0 ? "west" : "east"}`
                    : "")
                + " of the array centre."
              : ""}
            {" "}The file itself is never uploaded, so it is not on screen — drop
            the same STL in again to see it and it comes back in the place it was
            measured, or press Remove to take it out of the simulation.
          </Typography>
        ) : null}

        {neighbours.missing.length > 0 && <Alert severity="info">{onRoof ? tr(
          `Οι πλευρές ${neighbours.missing.join(", ")} χρειάζονται ολοκλήρωση και αποθήκευση της διάταξης. Οι αρχικές θέσεις είναι ενδεικτικές· επιλέξτε κάθε πλευρά για επεξεργασία πριν υπολογίσετε τις κοινές σκιάσεις.`,
          `Surfaces ${neighbours.missing.join(", ")} need their layouts completed and saved. Initial positions are provisional; select each surface for editing before calculating shared shading.`) : tr(
          `Λείπει κοινή θέση ή έγκυρη διάταξη για τις επιφάνειες: ${neighbours.missing.join(", ")}. Συμπληρώστε συντεταγμένες έργου σε όλες ή τοποθετήστε όλες στον χάρτη.`,
          `Common position or valid layout missing for surfaces: ${neighbours.missing.join(", ")}. Set project coordinates for all surfaces or place all on the map.`)}</Alert>}
        {!plantMode && !scene.tracker && !props.modulePositions?.length && <Alert severity="info">{tr(
          "Για μέτρηση ανά πλαίσιο ολοκληρώστε την αντιστοίχιση strings στις σειρές παρακάτω.",
          "Complete the string-to-row assignment below to measure individual modules.")}</Alert>}
        {meshFile ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <b>{meshFile.name}</b> — {describeMesh(meshFile.triangles, placed?.bounds)}
            {placed ? ` · base at ${placed.bounds.min[2].toFixed(1)} m, top at ${placed.bounds.max[2].toFixed(1)} m` : ""}
            {/* Named, because the classifier found them and then declined to
                treat them as the plant: a reader who can see "12 faces" in one
                place and no plant chip in another is owed the reason. */}
            {plant?.stray
              ? ` · ${plant.count} face${plant.count === 1 ? "" : "s"} happen to sit at the`
                + ` array's tilt (${plant.area.toFixed(1)} m²) — too little to be the plant,`
                + " so the model is treated as surroundings"
              : ""}
          </Typography>
        ) : null}

        {plantMode && plantLayout && (
          <Box
            sx={{
              p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider",
              display: "flex", flexWrap: "wrap", gap: 1.2, alignItems: "center",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
              Layout measured from the model
            </Typography>
            <Typography variant="caption" sx={{ flex: 1, minWidth: 260, color: "text.secondary" }}>
              {plantLayout.rows} row{plantLayout.rows === 1 ? "" : "s"}
              {plantLayout.pitch ? ` · ${plantLayout.pitch.toFixed(2)} m pitch` : " · pitch not measurable from one row"}
              {` · ${plantLayout.rowLength.toFixed(0)} m long`}
              {plantLayout.tilt != null
                ? ` · ${plantLayout.tilt.toFixed(0)}° / ${plantLayout.azimuth.toFixed(0)}°`
                : ""}
              {` · ${plantLayout.area.toFixed(0)} m² of collector`}
              {plantLayout.modules ? ` ≈ ${plantLayout.modules} modules` : ""}
              {plantLayout.footprint ? ` · ${plantLayout.footprint.toFixed(0)} m² footprint` : ""}
            </Typography>
            {onLayoutFromModel && (
              <MaybeTooltip
                arrow
                title={tr(
                  "Συμπληρώνει από το εισαγόμενο μοντέλο το βήμα, τις σειρές και την επιφάνεια εγκατάστασης. Η διάταξη αποθηκεύεται μόνο αφού πατήσετε Αποθήκευση. Η μέτρηση σκίασης χρησιμοποιεί ήδη το μοντέλο ανεξάρτητα από αυτή την επιλογή.",
                  "Fills the spacing inputs from the model: pitch, rows in the field and " +
                  "footprint. Nothing is saved until you press Save on the layout, and the " +
                  "shading measurement already uses the model whether or not you do - this " +
                  "is for the rest of the simulation, which reads the typed layout."
                )}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onLayoutFromModel(plantLayout)}
                >
                  {tr("Χρήση ως διάταξη", "Use as the layout")}
                </Button>
              </MaybeTooltip>
            )}
          </Box>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".stl,model/stl,application/sla"
          style={{ display: "none" }}
          onChange={(e) => loadFile(e.target.files?.[0])}
        />

        {meshError && <Alert severity="error" sx={{ py: 0 }}>{meshError}</Alert>}

        {(meshFile || siteMesh.obstacles || neighbours.triangles || hasModuleMoves || props.preparationIssue || props.detailedMode || allError
          || (props.shadingRequired && onMeasureAll)) && (
          <>
            {meshFile && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField
                select size="small" label="Up axis" value={placement.upAxis}
                onChange={(e) => setPlacement((p) => ({ ...p, upAxis: e.target.value }))}
                sx={{ minWidth: 118 }}
                helperText="Z-up for CAD"
              >
                <MenuItem value="z">Z-up (CAD)</MenuItem>
                <MenuItem value="y">Y-up</MenuItem>
              </TextField>
              <TextField
                select size="small" label="Units" value={placement.units}
                onChange={(e) => setPlacement((p) => ({ ...p, units: e.target.value }))}
                sx={{ minWidth: 100 }}
                helperText="check the height"
              >
                {Object.keys(UNIT_SCALES).map((u) => (
                  <MenuItem key={u} value={u}>{u}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small" type="number" label="North rotation (°)"
                value={placement.northRotationDeg}
                onChange={(e) => setPlacement((p) => ({ ...p, northRotationDeg: Number(e.target.value) || 0 }))}
                sx={{ width: 150 }} helperText="clockwise from the model's +Y"
              />
              <TextField
                size="small" type="number" label="East offset (m)" value={placement.east}
                onChange={(e) => setPlacement((p) => ({ ...p, east: Number(e.target.value) || 0 }))}
                sx={{ width: 130 }} helperText="+ east of the array"
              />
              <TextField
                size="small" type="number" label="North offset (m)" value={placement.north}
                onChange={(e) => setPlacement((p) => ({ ...p, north: Number(e.target.value) || 0 }))}
                sx={{ width: 132 }} helperText="− south of the array"
              />
              <TextField
                size="small" type="number" label="Base height (m)" value={placement.baseHeight}
                onChange={(e) => setPlacement((p) => ({ ...p, baseHeight: Number(e.target.value) || 0 }))}
                sx={{ width: 130 }} helperText="above the array's ground"
              />
              <TextField
                select size="small" label="Model sits on"
                value={placement.dropToGround ? "ground" : "roof"}
                onChange={(e) => setPlacement((p) => ({ ...p, dropToGround: e.target.value === "ground" }))}
                sx={{ minWidth: 214 }}
                helperText={placement.dropToGround
                  ? "lowest point set on the ground"
                  : "the model's zero is the roof"}
              >
                <MenuItem value="ground">Ground beside the array</MenuItem>
                <MenuItem value="roof">Roof under the array</MenuItem>
              </TextField>
            </Stack>}


            {!props.detailedMode && <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                variant="contained" size="small"
                disabled={props.moduleMappingValid === false || dualAxis || tableProgress != null || (!obstacleTriangles && !hasModuleMoves && !onMeasureAll) || neighbours.missing.length > 0 || (!plantMode && !scene.tracker && !props.modulePositions?.length) || Boolean(siteGeometryErrors(siteGeometry).length) || Boolean(roofStatus.outside || roofStatus.intersecting || roofStatus.belowRoof || roofStatus.moduleOverlap || roofStatus.invalidOffsets)}
                onClick={measureObstacle}
              >
                {tableProgress != null ? tr("Υπολογισμός…", "Calculating…") : tr("Υπολογισμός σκιάσεων", "Calculate shading")}
              </Button>
              {tableProgress != null && !overlayActive && (
                <>
                  {onMeasureAll && measureAllCount > 1 && <Typography variant="caption" color="text.secondary">
                    {tr(`Πλευρά ${Math.min(measureAllCount, Math.floor(tableProgress * measureAllCount) + 1)} από ${measureAllCount}`,
                      `Face ${Math.min(measureAllCount, Math.floor(tableProgress * measureAllCount) + 1)} of ${measureAllCount}`)}
                  </Typography>}
                  <Box sx={{ flex: 1, minWidth: 140 }}>
                    <LinearProgress variant="determinate" value={Math.round(tableProgress * 100)} />
                  </Box>
                  <Button size="small" color="inherit" onClick={() => { cancelRef.current = true; }}>
                    {tr("Ακύρωση", "Cancel")}
                  </Button>
                </>
              )}
            </Stack>}
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: -0.5 }}>
              {dualAxis ? <Typography variant="body2" color="text.secondary" role="status">
                {tr("Οι σκιάσεις διάταξης δεν υποστηρίζονται για tracker δύο αξόνων. Η σκίαση της μελέτης είναι το ποσοστό σκίασης στις απώλειες DC.",
                  "Layout shading is not supported for a dual-axis tracker. The study's shading is the shading percentage in the DC losses.")}
              </Typography> : (props.preparationIssue || allError) ? <Typography variant="body2" color="warning.main" role="status">
                {allError || scenePreparationMessage(props.preparationIssue, greek)}
              </Typography> : props.detailedMode ? <Typography variant="body2" color={props.scenePrepared ? "success.main" : "text.secondary"}>
                {props.scenePrepared
                  ? tr("Η γεωμετρία της λεπτομερούς μεθόδου έχει προετοιμαστεί και αποθηκευτεί.", "Detailed-method geometry has been prepared and saved.")
                  : tr("Η γεωμετρία της λεπτομερούς μεθόδου θα προετοιμαστεί με την «Αποθήκευση διάταξης».", "Detailed-method geometry will be prepared when you select Save layout.")}
              </Typography> : (obstacleTriangles || hasModuleMoves || (props.shadingRequired && onMeasureAll)) && (savedTable?.table && !savedTable.stale
                ? (savedTableUnsaved
                  ? <Typography variant="body2" color="info.main" role="status">{tr("Σκιάσεις υπολογισμένες · αποθηκεύονται με την «Αποθήκευση διάταξης»", "Shading calculated · stored by “Save layout”")}</Typography>
                  : <Typography variant="body2" color="success.main" role="status">{tr("Σκιάσεις υπολογισμένες και αποθηκευμένες με τη διάταξη", "Shading calculated and saved with the layout")}</Typography>)
                : props.shadingOutdated
                  ? <Typography variant="body2" color="warning.main" role="status" sx={{ fontWeight: 600 }}>{tr("Η διάταξη άλλαξε μετά τον υπολογισμό σκιάσεων · υπολογίστε ξανά", "The layout changed after the shading was calculated · calculate again")}</Typography>
                  : <Typography variant="body2" color="text.secondary" role="status">{tr("Δεν έχουν υπολογιστεί σκιάσεις για αυτή τη διάταξη", "No shading calculated for this layout yet")}</Typography>)}
              {!dualAxis && props.detailedMode && <Typography variant="body2" color="text.secondary">
                {tr("Με την «Αποθήκευση διάταξης» προετοιμάζονται και ελέγχονται η γεωμετρία και οι αντιστοιχίσεις των strings. Οι αναλυτικές σκιάσεις υπολογίζονται στην «Εκτίμηση» ή στην εκτέλεση προσομοίωσης της μελέτης. Μετά από αλλαγές στη διάταξη ή στα strings, αποθηκεύστε ξανά πριν εκτελέσετε νέο υπολογισμό.",
                  "Save layout prepares and validates the geometry and string mapping. Detailed shading is calculated during Estimate or the study simulation. After changing the layout or strings, save again before running a new calculation.")}
              </Typography>}
              {!dualAxis && !props.detailedMode && <Typography variant="caption" color="text.secondary">
                {onMeasureAll && measureAllCount > 1
                  ? tr(`Υπολογίζονται όλες οι πλευρές μαζί (${measureAllCount}), γιατί η σκίαση της καθεμιάς εξαρτάται από τις άλλες, και αποθηκεύονται όλες μαζί με την «Αποθήκευση διάταξης», η οποία δεν υπολογίζει σκιάσεις. Απαιτείται πριν από την προσομοίωση. Υπολογίστε ξανά μετά από κάθε αλλαγή σε οποιαδήποτε πλευρά. Χρειάζεται και για την τελική αναφορά σχεδίασης.`,
                    `Every face is calculated at once (${measureAllCount}), since each one's shading depends on the others, and they are all saved together by “Save layout”, which does not calculate shading. Required before the simulation. Calculate again after any change to any face. Also needed for the final design report.`)
                  : tr("Απαιτείται πριν από την προσομοίωση. Αποθηκεύεται μαζί με τη διάταξη, με την «Αποθήκευση διάταξης», η οποία δεν υπολογίζει σκιάσεις. Υπολογίστε ξανά μετά από κάθε αλλαγή της διάταξης. Χρειάζεται και για την τελική αναφορά σχεδίασης.",
                    "Required before the simulation. It is saved together with the layout, by “Save layout”, which does not calculate shading. Calculate again after any layout change. Also needed for the final design report.")}
              </Typography>}
            </Stack>

            {!props.detailedMode && !props.preparationIssue && !allError && (table || savedTable?.table)?.moduleShading &&
              <StringShadingSummary groups={groupModuleShading(withModuleValues(table || savedTable?.table).moduleShading)} greek={greek} />}
            {!props.detailedMode && table && tableProgress == null && !props.preparationIssue && !allError && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {greek ? <>
                  Μετρήθηκαν {table.cellsComputed} προσπελάσιμες θέσεις του ήλιου
                  {props.modulePositions?.length ? ` για ${props.modulePositions.length} πλαίσια` : ""}.
                  {" "}{table.trianglesUsed.toLocaleString("en-GB")} από τα {table.trianglesGiven.toLocaleString("en-GB")} τρίγωνα της σκηνής μπορούν να σκιάσουν τη συστοιχία.
                  {" "}{tableApplied
                    ? (savedTableUnsaved ? "Αποθηκεύεται με την «Αποθήκευση διάταξης» και εφαρμόζεται στην προσομοίωση." : "Αποθηκεύτηκε στο έργο και εφαρμόζεται στην προσομοίωση.")
                    : "Δεν έχει αποθηκευτεί στο έργο."}
                </> : <>Measured over{" "}
                {plantMode
                  ? `the modules found in the model — ${plant.count} faces over ` +
                    `${plant.rows} row${plant.rows === 1 ? "" : "s"}, ` +
                    `${plant.area.toFixed(0)} m² of collector`
                  : scene.field
                  ? `the whole field — ${scene.field.rowsGiven ? "" : "about "}${scene.field.rows} rows` +
                  `${scene.field.rowsGiven ? " (set in the layout)" : " (assumed square)"}` +
                  ` of ${scene.field.rowLength.toFixed(0)} m ` +
                    `(${scene.field.widthAcross.toFixed(0)} m across), sampled on ${scene.field.sampledRows} of them`
                  : "the rows on screen, because the module count is unknown"}
                , at {table.cellsComputed} sun positions the sun actually reaches at this
                latitude ({table.trianglesUsed.toLocaleString("en-GB")} of{" "}
                {table.trianglesGiven.toLocaleString("en-GB")} triangles could cast a shadow on the
                array; the rest sit below it).{" "}
                {tableApplied
                  ? (savedTableUnsaved
                    ? "Saved with “Save layout” — the simulation then applies it hour by hour."
                    : "Saved with the project — the simulation applies it hour by hour.")
                  : "Not stored with the project."}</>}
              </Typography>
            )}
          </>
        )}
      </Stack>

      {knownOffset == null && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Clock times are the zone meridian for this longitude (UTC{offsetHours >= 0 ? "+" : ""}
          {offsetHours}); the project timezone could not be read, so they may be up to an
          hour out. Sun angles and shading are unaffected.
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Where the sheet's four views are taken from.
 *
 * In front of the modules and 28 degrees off their axis -- far enough that the
 * rows read as rows rather than as one flat band, near enough that the sun is
 * on the camera's side of them for most of the day, which is what puts the
 * shadows in the picture instead of behind the rows.
 *
 * A tracker has no fixed surface azimuth; its axis does, and the modules face
 * across it.
 */
export function printedBearing(scene, camera) {
  const face = Number.isFinite(scene?.surfaceAzimuth)
    ? scene.surfaceAzimuth
    : (Number.isFinite(scene?.axisAzimuth) ? scene.axisAzimuth + 90 : null);
  if (face === null) return camera?.az ?? 205;
  return ((face + 28) % 360 + 360) % 360;
}

// For tests: the row builder and the parcel frame, which have no UI of their own.
export { buildRows as __buildRows, parcelFrame as __parcelFrame };
